import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { eachDayOfInterval, format } from "date-fns";

export const isEmptyObject = (obj) => Object.keys(obj).length > 0;

const normalizeSettingValue = (value) =>
    value === true || value === "true" || value === "1" || value === 1;

const parseFiringOrderList = (value) =>
    String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

const resolveFiringOrderLabels = (configuredValue, diagnosticOrder) => {
    const configuredOrder = parseFiringOrderList(configuredValue);
    if (configuredOrder.length > 0) {
        return configuredOrder;
    }

    if (!Array.isArray(diagnosticOrder)) {
        return [];
    }

    return diagnosticOrder
        .map((item) => String(item).trim())
        .filter(Boolean);
};

export const isTwistAngleEnabled = () => {
    if (typeof window === "undefined" || !window.sessionStorage) {
        return false;
    }

    return normalizeSettingValue(
        window.sessionStorage.getItem("twist_angle_page")
    );
};

export function buildSoketData(response, modelType, formData) {
    const firstKey = Object.keys(response)[0];
    const parserFormData = JSON.parse(formData);
    const maxRPMThrshhold = parseInt(parserFormData.rated_rpm);
    const maxPower = parseInt(parserFormData.power);
    const data = response[firstKey];
    let globalIndicator = [];
    let cylinder_specific_indicators = [];

    let isAlert = true;
    let alertData = [];
    if (modelType === "Engine") {
        globalIndicator.push(
            buildIndicatorData(
                "Engine Health",
                data?.MechanicalHealth,
                "valueInHealth"
            )
        );
        globalIndicator.push(
            buildIndicatorData(
                "Combustion Condition",
                data?.EngineEfficiency,
                "valueInHealth"
            )
        );
        globalIndicator.push(
            buildRpmData("RPM", data?.ChannelSpeed, maxRPMThrshhold)
        );
        
        // Always include Twist Angle if Acyclism data exists
        // Let UI components filter based on setting instead of filtering here
        if (
            data?.Acyclism &&
            data?.Acyclism.value !== undefined &&
            data?.Acyclism.value !== null
        ) {
            globalIndicator.push(
                buildIndicatorData(
                    "Twist Angle",
                    data?.Acyclism,
                    "value"
                )
            );
        }
        globalIndicator.push(
            buildIndicatorData(
                "Performance of Mounts & Supports",
                data?.Unbalance,
                "valueInHealth"
            )
        );
        // globalIndicator.push(
        //     buildIndicatorData(
        //         "Governor, Crank driven Accessories Health",
        //         data?.CamPump,
        //         "valueInHealth"
        //     )
        // );
        globalIndicator.push(
            buildIndicatorData(
                "Performance of Vibration Damper",
                data?.Damper,
                "valueInHealth"
            )
        );
        /* The above code is adding an indicator to a global indicator array. The indicator represents
        an increase in fuel consumption and includes data for power loss. The indicator is built
        using the buildIndicatorData function, which takes in the indicator name, the power loss
        data, the value type, and a boolean indicating if the indicator is active. */
        globalIndicator.push(
            buildIndicatorData(
                "Increase in Fuel Consumption",
                data?.PowerLoss,
                "value",
                true
            )
        );

        // Build cylinder-specific indicators from WebSocket data
        try {
            // WebSocket payload may keep FiringOrder inside Engine object.
            const diagnosticFiringOrder = data?.Engine?.FiringOrder || data?.FiringOrder || [];
            const firingOrderLabel = resolveFiringOrderLabels(
                parserFormData?.firing_order,
                diagnosticFiringOrder
            );

            if (firingOrderLabel.length > 0) {
                const firingOrderSplit = Math.ceil(firingOrderLabel.length / 2);
                const first = firingOrderLabel.slice(0, firingOrderSplit);
                const second = firingOrderLabel.slice(first.length);

                // Compression Condition
                if (data?.Compression) {
                    const compression = buildCompressionData(
                        first,
                        second,
                        data["Compression"],
                        "Compression Condition",
                        firingOrderLabel
                    );
                    if (compression) {
                        cylinder_specific_indicators.push(compression);
                    }
                }

                // Injection Condition
                const injectionData = data?.InjectionCondition || data?.Injection;
                if (injectionData) {
                    const injectionCondition = buildCompressionData(
                        first,
                        second,
                        injectionData,
                        "Injection Condition",
                        firingOrderLabel
                    );
                    if (injectionCondition) {
                        const injectionConditionWithTooltip = { ...injectionCondition };
                        injectionConditionWithTooltip.tooltip = trendTooltip["Injection Condition"];
                        cylinder_specific_indicators.push(injectionConditionWithTooltip);
                    }
                }

                // Bearing Condition
                if (data?.Bearing) {
                    const bearingCondition = buildCompressionData(
                        first,
                        second,
                        data["Bearing"],
                        "Bearing Condition",
                        firingOrderLabel
                    );
                    if (bearingCondition) {
                        cylinder_specific_indicators.push(bearingCondition);
                    }
                }

                // Condition of cyl moving parts
                if (data?.BearingBis) {
                    const conditionCylMovingParts = buildCompressionData(
                        first,
                        second,
                        data["BearingBis"],
                        "Condition of cyl moving parts",
                        firingOrderLabel
                    );
                    if (conditionCylMovingParts) {
                        cylinder_specific_indicators.push(conditionCylMovingParts);
                    }
                }

                // Misfiring (for certain fuel types)
                if (
                    parserFormData["fuel"] === "Hydrogen" ||
                    parserFormData["fuel"] === "Ammonia" ||
                    parserFormData["fuel"] === "Natural Gas"
                ) {
                    if (data?.Misfiring) {
                        const misfiring = buildCompressionData(
                            first,
                            second,
                            data["Misfiring"],
                            "Misfiring",
                            firingOrderLabel
                        );
                        if (misfiring) {
                            cylinder_specific_indicators.push(misfiring);
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Error building cylinder-specific indicators from WebSocket:", error);
        }
    } else if (modelType === "Torque") {
        isAlert = false;
        globalIndicator.push(
            buildRpmData("RPM", data.ChannelSpeed || 0, maxRPMThrshhold)
        );
        globalIndicator.push(
            buildTorqueIndicatorData(
                "Torque(kNm)",
                (data.StaticTorque?.value || 0) * 0.001,
                4000
            )
        );

        globalIndicator.push(
            buildTorqueIndicatorData(
                "Power(kW)",
                (data.StaticPower?.value || 0) * 0.001,
                maxPower * 0.001
            )
        );
        globalIndicator.push(
            buildTorqueIndicatorData(
                "Torsion(degree)",
                data.StaticTorsion?.value || 0,
                6
            )
        );
    } else if (modelType === "Turbine") {
        globalIndicator.push(
            buildRpmData("Speed", data?.ChannelSpeed, maxRPMThrshhold)
        );
        globalIndicator.push(
            buildIndicatorData(
                "Regularity/Deviation",
                data?.RegularityDeviation,
                "valueInHealth"
            )
        );
        if (data?.BearingStatus && data?.BearingStatus.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Bearing Status",
                    data?.BearingStatus,
                    "valueInHealth"
                )
            );
        }
        if (data?.BearingStatusGas && data?.BearingStatusGas.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Bearing Status",
                    data?.BearingStatusGas,
                    "valueInHealth"
                )
            );
        }
        globalIndicator.push(
            buildIndicatorData("Shaft/Blade Health", data?.BladeStatus, "valueInHealth")
        );
        if (JSON.parse(formData).type === "Steam") {
            globalIndicator.push(
                buildIndicatorData("Coupling", data?.TurbineCoupling, "valueInHealth")
            );

        } else { //For Gas Turbine, the indicator list is updated in v1.1.11
            globalIndicator.push(
                buildIndicatorData("Coupling", data?.TurbineCoupling, "valueInHealth")
            );
            globalIndicator.push(
                buildIndicatorData(
                    "Combustion Kit",
                    data?.CombustionKit,
                    "valueInHealth"
                )
            );
        }
    } else if (modelType === "Propeller") {
        globalIndicator.push(
            buildRpmData("Speed", data?.ChannelSpeed, maxRPMThrshhold)
        );

        // const efficiency = Number(data?.PropellerEfficiency);
        // const roundedEfficiency = Math.floor(efficiency * 1000) / 1000; // rounds down to 3 decimals
        //  globalIndicator.push(
        //    buildIndicatorData(
        //      "Drop in Efficiency",
        //      data?.PropellerEfficiency,
        //      "value",
        //      false,
        //      "Propeller"
        //    )
        //  );
        globalIndicator.push(
            buildIndicatorData(
                "Propeller Fouling",
                data?.PropellerFouling || data?.PropellerFooling,
                "value",
                true,
                "Propeller"
            )
        );
        // globalIndicator.push(
        //     buildIndicatorData("Propeller Unbalance", data?.PropellerUnbalance, "valueInPercent", false, "Propeller")
        // );
        // globalIndicator.push(
        //     buildIndicatorData("Stern Tube Bearing Health", data?.PropellerBearing, "valueInHealth", false, "Propeller")
        // );

    } else if (modelType === "Motor") {
        globalIndicator.push(
            buildIndicatorData(
                "Electromagnetic Stress",
                data?.MElectromag,
                "valueInHealth"
            )
        );
        globalIndicator.push(
            buildIndicatorData("Bearing", data?.MBearing, "valueInHealth")
        );
        globalIndicator.push(
            buildIndicatorData("Stability", data?.MStressStability, "valueInHealth")
        );
        globalIndicator.push(
            buildRpmData("Speed", data?.ChannelSpeed, maxRPMThrshhold)
        );

    } else if (modelType === "Bearing") {
        globalIndicator.push(
            buildIndicatorData(
                "Mechanical Health",
                data?.BearingGlobal,
                "valueInHealth"
            )
        );
        globalIndicator.push(
            buildTorqueIndicatorData(
                "Level RMS(mm/s)",
                data.GlobalLevel?.value || 0,
                10
            )
        );
        globalIndicator.push(
            buildIndicatorData("Shock Index", data?.GlobalKurto, "valueInHealth")
        );
        globalIndicator.push(
            buildIndicatorData("Bearings", data["4KMixed"], "valueInHealth")
        );
        globalIndicator.push(
            buildRpmData("RPM", data?.ChannelSpeed, maxRPMThrshhold)
        );
        globalIndicator.push(
            buildIndicatorData("Friction", data["8KMixed"], "valueInHealth")
        );
        globalIndicator.push(
            buildIndicatorData("Shaft/Clearance", data["2KMixed"], "valueInHealth")
        );
        globalIndicator.push(
            buildIndicatorData(
                "Global(Umbalance/Alignment/Loosness)",
                data?.GlobalMixed,
                "valueInHealth"
            )
        );
    } else if (modelType === "Turbocharger") {
        globalIndicator.push(
            buildIndicatorData("Shock index", data?.GlobalKurto, "valueInPercent", true)
        );
        globalIndicator.push(
            buildTorqueIndicatorData(
                "Level RMS(mm/s)",
                data.GlobalLevel?.value || 0,
                10
            )
        );
        globalIndicator.push(
            buildIndicatorData("Bearing Damage", data["4KMixed"], "valueInPercent", true)
        );
        globalIndicator.push(
            buildIndicatorData(
                "Unbalance Instability",
                data?.GlobalMixed,
                "valueInPercent",
                true
            )
        );
    }

    return {
        isAlert,

        globalIndicator,

        cylinder_specific_indicators,

        currentStatus: data["Status"],

        globalIndicatorTime: firstKey,

        alertData: alertData,
    };
}

function getConditionalIndicatorValue(isGradientOpposite, value) {
    return isGradientOpposite
        ? "" + parseFloat(value).toFixed(2)
        : "" + parseInt(value);
}

function buildIndicatorData(indicator_title, data, key, isGradientOpposite, modelType) {
    let isOffline = false;
    const isTwistAngleIndicator = indicator_title === "Twist Angle";
    
    if (!data || data[key] === undefined || data[key] === null) {
        isOffline = true;
    }
    let value = data ? data[key] : 0;

    // Convert value to a number if it's a string
    value = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(value)) {
        console.warn(`Warning: Invalid value encountered for ${indicator_title}, setting to 0`);
        value = 0; // Ensure it's a valid number
    }

    if (
        key !== "value" &&
        key !== "valueInPercent" &&
        modelType !== "Propeller" &&
        !isTwistAngleIndicator
    ) {
        value = roundToNearest10(value);
    }

    let indicatorUnit = "Stable";
    let indicatorType = "success";

    // **Propeller: 0 = Healthy, 1 = Bad**
    if (indicator_title === "Propeller Unbalance") {
        if (value <= 30) {
            indicatorUnit = "Stable";
            indicatorType = "success";
        } else if (value > 30 && value < 70) {
            indicatorUnit = "Attention";
            indicatorType = "warning";
        } else if (value > 70 && value <= 100) {
            indicatorUnit = "Alert";
            indicatorType = "error";
        }
    } else if (indicator_title === "Propeller Fouling") {
        if (value <= 0.3) {
            indicatorUnit = "Stable";
            indicatorType = "success";
        } else if (value > 0.3 && value <= 0.7) {
            indicatorUnit = "Attention";
            indicatorType = "warning";
        } else if (value > 0.7) {
            indicatorUnit = "Alert";
            indicatorType = "error";
        }
    } else if (isTwistAngleIndicator) {
        if (value <= 0.25) {
            indicatorUnit = "Stable";
            indicatorType = "success";
        } else if (value >= 0.3 && value <= 0.4) {
            indicatorUnit = "Attention";
            indicatorType = "warning";
        } else if (value > 0.4) {
            indicatorUnit = "Alert";
            indicatorType = "error";
        } else {
            indicatorUnit = "Stable";
            indicatorType = "success";
        }
    }
    else {
        if (key === "value") {
            if (value <= 2) {
                indicatorUnit = "Stable";
                indicatorType = "success";
            } else if (value >= 2 && value < 4) {
                indicatorUnit = "Attention";
                indicatorType = "warning";
            } else if (value >= 4 && value <= 5) {
                indicatorUnit = "Alert";
                indicatorType = "error";
            }
        } else {
            if (isGradientOpposite) {
                if (value <= 30) {
                    indicatorUnit = "Stable";
                    indicatorType = "success";
                } else if (value > 30 && value < 70) {
                    indicatorUnit = "Attention";
                    indicatorType = "warning";
                } else {
                    indicatorUnit = "Alert";
                    indicatorType = "error";
                }
            } else {
                if (value <= 30) {
                    indicatorUnit = "Alert";
                    indicatorType = "error";
                } else if (value > 30 && value < 70) {
                    indicatorUnit = "Attention";
                    indicatorType = "warning";
                }
            }
        }
    }

    return {
        indicatorName: indicator_title,
        indicatorMin: 0,
        indicatorMax: isTwistAngleIndicator
            ? 1.5
            : indicator_title === "Propeller Fouling"
                ? 1
                : (indicator_title === "Drop in Efficiency" || key === "value")
                    ? 5
                    : (key === "valueInHealth" || key === "valueInPercent")
                        ? 100
                        : isGradientOpposite
                        ? 10
                        : 100,
        indicatorValue: isOffline
            ? "Offline"
            : isTwistAngleIndicator
                ? parseFloat(value).toFixed(4)
            : (indicator_title === "Drop in Efficiency")
                ? value.toFixed(2)  // **Ensures decimal values**
                : (key === "valueInHealth" || key === "valueInPercent")
                    ? "" + parseInt(value)
                    : getConditionalIndicatorValue(isGradientOpposite, value),
        isPercentage:
            !isOffline &&
            indicator_title !== "Drop in Efficiency" &&
            indicator_title !== "Propeller Fouling" &&
            !isTwistAngleIndicator,
        indicatorUnit: isOffline ? " " : (indicator_title === "Drop in Efficiency" ? "" : indicatorUnit),
        isGradientColor: true,
        indicatorType: isOffline ? " " : indicatorType,
        isGradientOpposite: isTwistAngleIndicator ? true : (isGradientOpposite ?? false),
        tooltip: trendTooltip[indicator_title],
    };
}

function buildRpmData(indicator_title, data, maxValue) {
    return {
        indicatorName: indicator_title,
        indicatorMin: 0,
        indicatorMax: maxValue || parseInt(data) * 2,
        indicatorValue: data ? parseInt(data) : 0,
        isPercentage: false,
        isGradientColor: false,
        indicatorType: "error",
        tooltip: trendTooltip[indicator_title],
    };
}

function buildTorqueIndicatorData(indicator_title, data, maxValue) {
    return {
        indicatorName: indicator_title,
        indicatorMin: 0,
        indicatorMax: maxValue || parseInt(data) * 2,
        indicatorValue: data ? parseFloat(data).toFixed(2) : 0,
        isPercentage: false,
        isGradientColor: false,
        indicatorType: "error",
        tooltip: trendTooltip[indicator_title],
    };
}

function buildPeekPressureChart(data, firingOrder, maxPressure) {
    let labels = [];
    for (let item of firingOrder) {
        labels.push("C" + item);
    }
    const increase_fuel_consumption = {
        trendsName: "Peak Pressure(Bar)",
        datapoints: data?.cylinderValues || [],
        labels: labels,
        chartType: "bar",
        xLabel: "Peak Pressure(Bar)",
        yLabel: "Cylinders",
        yMax: round(maxPressure),
    };
    return increase_fuel_consumption;
}

export function buildData(response) {
    const from_data = response["from_data"];
    const maxPower = from_data?.power;
    const alertData = response["alertData"];
    const historical_data = response["historical_data"];
    let data = {};

    if (!historical_data || historical_data.length === 0) {
        return {
            cylinder_specific_indicators: [],
            pressureReadings: [],
            trends: [],
        };
    }
    // process engine data
    if (response["type"] === "Engine") {
        data = JSON.parse(historical_data.at(-1)?.jsondata);
        const moduleData = data["Engine"] || {};
        const firingOrderLabel = resolveFiringOrderLabels(
            from_data?.firing_order,
            moduleData["FiringOrder"]
        );
        const firingOrderSplit = Math.ceil(firingOrderLabel.length / 2);
        const first = firingOrderLabel.slice(0, firingOrderSplit);
        const second = firingOrderLabel.slice(first.length);

        let cylinder_specific_indicators = [];
        if (data?.Compression) {
            const compression = buildCompressionData(
                first,
                second,
                data["Compression"],
                "Compression Condition",
                firingOrderLabel
            );
            if (compression) {
                cylinder_specific_indicators.push(compression);
            }
        }
        if (data?.InjectionCondition) {
            const injection_Condition = buildCompressionData(
                first,
                second,
                data["InjectionCondition"],
                "Injection Condition",
                firingOrderLabel
            );
            if (injection_Condition) {
                // Create an object to add the tooltip
                const injectionConditionWithTooltip = { ...injection_Condition };

                // Add the tooltip for "Injection Condition"
                injectionConditionWithTooltip.tooltip = trendTooltip["Injection Condition"]; // Add your tooltip text here

                cylinder_specific_indicators.push(injectionConditionWithTooltip);
            }
        }
        if (data?.Bearing) {
            const bearing_Condition = buildCompressionData(
                first,
                second,
                data["Bearing"],
                "Bearing Condition",
                firingOrderLabel
            );
            if (bearing_Condition) {
                cylinder_specific_indicators.push(bearing_Condition);
            }
        }
        if (data?.BearingBis) {
            const condition_of_cyl_moving_parts = buildCompressionData(
                first,
                second,
                data["BearingBis"],
                "Condition of cyl moving parts",
                firingOrderLabel
            );
            if (condition_of_cyl_moving_parts) {
                cylinder_specific_indicators.push(condition_of_cyl_moving_parts);
            }
        }
        if (
            from_data["fuel"] === "Hydrogen" ||
            from_data["fuel"] === "Ammonia" ||
            from_data["fuel"] === "Natural Gas"
        ) {
            const miss_firing = buildCompressionData(
                first,
                second,
                data["Misfiring"],
                "Misfiring",
                firingOrderLabel
            );
            if (miss_firing) {
                cylinder_specific_indicators.push(miss_firing);
            }
        }
        /*{
            const pressure = buildCompressionData(
                first,
                second,
                data["Pressure"],
                "Pressure",
                firingOrderLabel
            );
            if (pressure) {
                cylinder_specific_indicators.push(pressure);
            }
        }*/
        let pressureReadings = [];
        if (data?.Pressure && data.Pressure.cylinderValues && firingOrderLabel.length > 0) {
            const firingOrder = moduleData.FiringOrder;
            const cylinderValues = data.Pressure.cylinderValues;

            for (let i = 0; i < Math.min(firingOrderLabel.length, cylinderValues.length); i++) {
                const cylinderValue = cylinderValues[i];

                if (cylinderValue > 0) { // Rohit Added on 20 May if statement (Check if pressure is greater than zero)
                    pressureReadings.push({
                        order: firingOrderLabel[i],
                        value: cylinderValue.toString(),
                    });
                }
            }
        }

        let trends = [];
        const increase_fuel_consumption = buildLineGradientChart(
            historical_data,
            "PowerLoss",
            "Increase in fuel consumption"
        );
        trends.push(increase_fuel_consumption);

        const mechanical_health = buildLineGradientChart(
            historical_data,
            "MechanicalHealth",
            "Engine Health",
            true
        );

        trends.push(mechanical_health);
        const peakPressure = data["Pressure"];
        if (peakPressure && parseInt(peakPressure["value"]) > 0) {
            const peak_pressure = buildPeekPressureChart(
                peakPressure,
                firingOrderLabel,
                from_data["max_pressure"]
            );
            trends.push(peak_pressure);
        }
        return {
            cylinder_specific_indicators: cylinder_specific_indicators,
            trends: trends,
            pressureReadings: pressureReadings,
            alert: buildEngineAlertData(alertData),
            alertUpdatedOn: new Date(),
        };
    }
    // process Torque data
    if (response["type"] === "Torque") {
        let trends = [];
        const torsionWithRpm = buildLineChart(
            historical_data,
            "StaticTorque",
            "Torque with RPM",
            false,
            true,
            1000
        );
        trends.push(torsionWithRpm);

        const powerWithRpm = buildLineChart(
            historical_data,
            "StaticPower",
            "Power with RPM",
            false,
            true,
            maxPower * 0.001
        );
        trends.push(powerWithRpm);

        return {
            cylinder_specific_indicators: [],
            trends,
            pressureReadings: [],
            alert: buildTorqueAlertData(historical_data),
            alertUpdatedOn: new Date(),
        };
    }
    // process Turbine data
    if (response["type"] === "Turbine") {
        if (from_data.type === "Steam") {
            let trends = [];
            const steamTurbineChart1 = buildTurbineChart(
                historical_data,
                "RegularityDeviation",
                "BladeStatus",
                "Regularity Deviation, Shaft/Blade Health",
                true
            );
            trends.push(steamTurbineChart1);

            const steamTurbineChart2 = buildTurbineChart(
                historical_data,
                "BearingStatus",
                "TurbineCoupling",
                "Bearing status, Coupling",
                true
            );
            trends.push(steamTurbineChart2);
            return {
                cylinder_specific_indicators: [],
                trends: trends,
                alert: buildTurbineAlertData(historical_data),
                alertUpdatedOn: new Date(),
            };
        } else {
            let trends = [];
            const gasTurbineChart1 = buildTurbineChart(
                historical_data,
                "RegularityDeviation",
                "BladeStatus",
                "Regularity Deviation, Shaft/Blade Health",
                true
            );
            trends.push(gasTurbineChart1);

            const gasTurbineChart2 = buildTurbineChart(
                historical_data,
                "BearingStatusGas",
                "CombustionKit",
                //TurbineCoupling added & removed in Dashboard trends page
                "Bearing status, Combustion Kit Status",
                true
            );
            trends.push(gasTurbineChart2);
            return {
                cylinder_specific_indicators: [],
                trends: trends,
                alert: buildTurbineAlertData(alertData),
                alertUpdatedOn: new Date(),
            };
        }
    }
    // process Propeller
    if (response["type"] === "Propeller") {
        let trends = [];
        // const propellerChart1 = buildPropeller1Chart(
        //     historical_data,
        //     "PropellerEfficiency",
        //     null,
        //     "Drop in Propeller Efficiency",
        //     true
        // );
        const propellerChart1 = buildPropeller1Chart(
            historical_data,
            "PropellerFouling",
            null,
            "Propeller Fouling",
            true
        );
        trends.push(propellerChart1);

        const propellerChart2 = buildPropellerChart(
            historical_data,
            "PropellerUnbalance",
            // "PropellerBearing",
            null,
            "Propeller Unbalance",
            // "Stern Tube Bearing",
            true
        );
        trends.push(propellerChart2);
        return {
            cylinder_specific_indicators: [],
            trends: trends,
            alert: buildPropellerAlertData(alertData),
            alertUpdatedOn: new Date(),
        };
    }
    // process Motor data
    if (response["type"] === "Motor") {
        let trends = [];
        const motorChart1 = buildMotorChart(
            historical_data,
            "MElectromag",
            null,
            "Electromagnetic Stress",
            true
        );
        trends.push(motorChart1);

        const motorChart2 = buildMotorChart(
            historical_data,
            "MBearing",
            "MStressStability",
            "Bearing , Stability",
            true
        );
        trends.push(motorChart2);

        return {
            cylinder_specific_indicators: [],
            trends: trends,
            alert: buildMotorAlertData(alertData),
            alertUpdatedOn: new Date(),
        };
    }
    // process Bearing data
    if (response["type"] === "Bearing") {
        let trends = [];
        const bearingChart1 = buildBearing1Chart(
            historical_data,
            "GlobalLevel",
            null,
            "Level(RMS)",
            true
        );
        trends.push(bearingChart1);

        const bearingChart2 = buildBearingChart(
            historical_data,
            "BearingGlobal",
            "GlobalKurto",
            " Mechanical health, Shock Index",
            true
        );
        trends.push(bearingChart2);
        return {
            cylinder_specific_indicators: [],
            trends: trends,
            alert: buildBearingAlertData(alertData),
            alertUpdatedOn: new Date(),
        };
    }

    if (response["type"] === "Turbocharger") {
        let trends = [];
        const turbochargerChart1 = buildTurbochargerChart(
            historical_data,
            "GlobalLevel",
            "GlobalKurto",
            "Level RMS(mm/s), Shock index",
            true,
            "value",
            "valueInPercent"
        );
        trends.push(turbochargerChart1);

        const turbochargerChart2 = buildTurbochargerChart(
            historical_data,
            "GlobalMixed",
            "4KMixed",
            "Unbalance Instability, Bearing Damage",
            true,
            "valueInPercent",
            "valueInPercent"
        );
        trends.push(turbochargerChart2);
        return {
            cylinder_specific_indicators: [],
            trends: trends,
            alert: buildTurbochargerAlertData(alertData),
            alertUpdatedOn: new Date(),
        };
    }

    return {
        cylinder_specific_indicators: [],
        pressureReadings: [],
        trends: [],
    };
}

function checkFillColor(value) {
    if (value < 27) {
        return "red";
    }
    if (value >= 27 && value < 45) {
        return "dark_orange";
    }
    if (value >= 45 && value < 67) {
        return "orange";
    }
    if (value >= 67 && value < 80) {
        return "light_green";
    }
    if (value >= 80) {
        return "success";
    }
    return "success";
}

function buildCompressionData(
    first,
    second,
    compressionData,
    graphLabel,
    firingOrderLabel
) {
    let children = [];
    const showSecondValue = 100 / second.length;

    const cylinderHealth = compressionData?.cylinderHealth;
    if (!cylinderHealth) {
        return null;
    }
    for (let i = 0; i < first.length; i++) {
        const compression = cylinderHealth[i];
        let firstChild = {
            name: "Cyl " + firingOrderLabel[i],
            fill: checkFillColor(compression),

            showValue: compression,
            children: [],
        };

        for (let j = 0; j < second.length; j++) {
            if (i === j) {
                const secondCompression = cylinderHealth[first.length + j];
                firstChild["children"].push({
                    name: "Cyl " + firingOrderLabel[first.length + j],
                    value: showSecondValue,
                    fill: checkFillColor(secondCompression),
                    showValue: secondCompression,
                });
            }
        }
        if (i === first.length - 1 && first.length < second.length) {
            for (let k = first.length; k < second.length; k++) {
                const secondCompression = cylinderHealth[k];
                firstChild.children[0].children = [
                    {
                        name: "Cyl " + firingOrderLabel[firingOrderLabel.length - 1],
                        value: showSecondValue,
                        fill: checkFillColor(secondCompression),
                        showValue: secondCompression,
                    },
                ];
            }
        }

        if (firstChild.children.length === 0) {
            firstChild.value = showSecondValue;
        }

        children.push(firstChild);
    }
    const compression = {
        graphLabel: graphLabel,
        graphData: [
            {
                name: "Global",
                fill: checkFillColor(compressionData["valueInHealth"]),
                showValue: compressionData["valueInHealth"],
                children: children,
            },
        ],
    };
    return compression;
}

export function buildLiveStatusData(data) {
    let currentStep = 1;
    let stepProgress = 0;
    let secondsLeft = "";
    let processingPercent = "";
    if (data) {
        const arr = data.split("\n");
        // Parse key=value pairs from the log file
        const fields = {};
        for (const line of arr) {
            const eqIndex = line.indexOf("=");
            if (eqIndex !== -1) {
                const key = line.substring(0, eqIndex).trim();
                const value = line.substring(eqIndex + 1).trim();
                fields[key] = value;
            }
        }

        // Extract SecondsLeftRecording (strip file path if present)
        if (fields["SecondsLeftRecording"]) {
            let rawSeconds = fields["SecondsLeftRecording"];
            // Remove file path like "into Log/PROCESS1_..." 
            const intoIndex = rawSeconds.indexOf(" into ");
            if (intoIndex !== -1) {
                rawSeconds = rawSeconds.substring(0, intoIndex) + " seconds";
            }
            secondsLeft = rawSeconds;
        }

        // Extract ProcessingTimePercent
        if (fields["ProcessingTimePercent"]) {
            processingPercent = fields["ProcessingTimePercent"];
        }

        // Determine step based on S1 field (RECORDING has highest priority)
        const s1 = fields["S1"] || "";
        if (s1.includes("RECORDING")) {
            currentStep = 2;
            processingPercent = ""; // Clear any stale processing data
            // Calculate progress from SecondsLeftRecording and SecondsBetweenProcessing
            const secondsLeftVal = parseFloat(secondsLeft);
            const totalTime = parseFloat(fields["SecondsBetweenProcessing(s)"]);
            if (!isNaN(secondsLeftVal) && !isNaN(totalTime) && totalTime > 0) {
                let per = percentage(secondsLeftVal, totalTime);
                if (per === 100) {
                    per = 0;
                }
                stepProgress = 100 - per;
            }
        } else if (s1.includes("WAITING")) {
            currentStep = 1;
            stepProgress = 0;
            processingPercent = "";
        } else if (
            s1.includes("START PROCESSING") ||
            s1.includes("Processing Diagmot")
        ) {
            // Check if diag file already says END PROCESSING (diag is more up-to-date)
            const processingMessage = fields["ProcessingMessage"] || "";
            if (processingMessage.includes("END PROCESSING")) {
                currentStep = 4;
                stepProgress = 100;
            } else {
                currentStep = 3;
                stepProgress = 100;
            }
        } else if (s1.includes("END PROCESSING")) {
            currentStep = 4;
            stepProgress = 100;
        } else {
            // Fallback: check ProcessingMessage only when S1 doesn't give a clear state
            const processingMessage = fields["ProcessingMessage"] || "";
            if (processingMessage.includes("Request for MANUALDIAG")) {
                currentStep = 1;
                stepProgress = 0;
            } else if (processingMessage.includes("START PROCESSING") ||
                processingMessage.includes("Processing Diagmot")) {
                currentStep = 3;
                stepProgress = 100;
            } else if (processingMessage.includes("END PROCESSING")) {
                currentStep = 4;
                stepProgress = 100;
            }
        }
    }

    return {
        currentStep: currentStep,
        currentMode: "Auto",
        stepProgress: parseInt(stepProgress),
        secondsLeft: secondsLeft,
        processingPercent: processingPercent,
        currentMessage: "Initiate Manual Measurement",
    };
}

export function buildSignalData(data, formData, type) {
    let firstChannelName = "";
    let secondChannelName = "";
    formData = JSON.parse(formData);
    if (type === "Engine") {
        firstChannelName = "Crankshaft";
        if (formData["CamShaft_SENSORx"] !== "No Channel") {
            secondChannelName = "CamShaft";
        }
        if (formData["TDC_SENSORx"] !== "No Channel") {
            secondChannelName = "TDC";
        }
        if (formData["Peak_Pressure_SENSORx"] !== "No Channel") {
            secondChannelName = "Peak Pressure - RPM Signal";
        }
    }
    if (type === "Torque") {
        firstChannelName = "DE Channel";
        if (formData["nde_channel_sensorx"] !== "No Channel") {
            secondChannelName = "NDE Channel";
        }
    }
    if (type === "Turbine") {
        firstChannelName = "Sensor";
    }
    if (type === "Propeller") {
        firstChannelName = "Sensor";
    }
    if (type === "Motor") {
        firstChannelName = "Sensor";
    }

    if (type === "Bearing") {
        firstChannelName = "Sensor";
    }

    if (type === "Turbocharger") {
        firstChannelName = "Sensor";
    }

    let returnArray = [];

    let i = 0;
    for (const item of data) {
        const firstKey = Object.keys(item)[0];
        const itemData = item[firstKey];

        if (!itemData.hasOwnProperty("Status")) {
            const itemDataFirstKey = Object.keys(itemData)[0];
            const channelData = itemData[itemDataFirstKey];
            returnArray.push({
                title: i === 0 ? firstChannelName : secondChannelName,
                value: channelData["ChannelSpeed"],
            });
        }
        i++;
    }
    return returnArray;
}

export function getCommaSepratedChannel(data, type) {
    data = JSON.parse(data);
    if (type === "Engine") {
        let channel = buildChannelName(data["Crankshaft_SENSORx"]);
        if (data["CamShaft_SENSORx"] !== "No Channel") {
            return channel + "," + buildChannelName(data["CamShaft_SENSORx"]);
        }
        if (data["TDC_SENSORx"] !== "No Channel") {
            return channel + "," + buildChannelName(data["TDC_SENSORx"]);
        }
        if (data["Peak_Pressure_SENSORx"] !== "No Channel") {
            return channel + "," + buildChannelName(data["Peak_Pressure_SENSORx"]);
        }
        return channel;
    }
    if (type === "Torque") {
        let channel = buildChannelName(data["de_channel_sensorx"]);
        if (data["nde_channel_sensorx"] !== "No Channel") {
            channel = channel + "," + buildChannelName(data["nde_channel_sensorx"]);
        }
        return channel;
    }
    if (type === "Turbine") {
        return buildChannelName(data["turbine_crankshaft_sensorx"]);
    }
    if (type === "Propeller") {
        return buildChannelName(data["propeller_crankshaft_sensorx"]);
    }
    if (type === "Motor") {
        return buildChannelName(data["motor_crankshaft_sensorx"]);
    }

    if (type === "Bearing") {
        return buildChannelName(data["bearing_crankshaft_sensorx"]);
    }

    if (type === "Turbocharger") {
        return buildChannelName(data["bearing_crankshaft_sensorx"]);
    }
}

function buildChannelName(channel) {
    return "CHANNEL" + channel.substr(channel.length - 1);
}

function buildLineGradientChart(data, key, title, isGradientOpposite) {
    let labels = [];
    let datapoints = [];
    let zAxisDataPoints = [];
    let count = 0;
    let hasAuxPowerPercent = false; // Flag to check presence of AUX.PowerPercent.Value

    if (data) {
        for (let item of data) {
            const moduleData = JSON.parse(item["jsondata"]);

            const valueObject = moduleData[key];
            if (valueObject) {

                /**Rohit  update 8 May */
                const auxPowerPercent = Object.keys(moduleData)
                    .filter(k => k.startsWith("Aux"))
                    .map(k => parseInt(moduleData[k]?.PowerPercent?.Value))
                    .find(v => !isNaN(v) && v > 0) || 0;
                const channelSpeed = parseInt(moduleData.ChannelSpeed || 0);

                if (auxPowerPercent) {
                    zAxisDataPoints.push("" + auxPowerPercent);
                    hasAuxPowerPercent = true; // Set flag if AUX.PowerPercent.Value is present
                } else {
                    zAxisDataPoints.push("" + channelSpeed);
                }

                labels.push(getdate(moduleData?.DateAndTime));

                if (isGradientOpposite && valueObject) {
                    datapoints.push(round(valueObject?.valueInHealth || 0));
                } else {
                    datapoints.push(round(valueObject?.value || 0));
                }
            }
            count++;
        }
    }
    let sum = 0;
    for (let dp of datapoints) {
        sum += parseInt(dp, 10); //don't forget to add the base
    }

    let avg = sum / datapoints.length;

    return {
        trendsName: title,
        /**Rohit  update 6 Jun */
        speedName: hasAuxPowerPercent ? "Power" : "Speed", // Change speedName based on presence of AUX.PowerPercent.Value
        min: round(Math.min(...datapoints)),
        max: round(Math.max(...datapoints)),
        yMax: title === "Engine Health" ? 110 : 10, //increaseinFuelConsump trend scale to 10
        avg: round(avg),
        datapoints: datapoints,
        dataPointsY1: zAxisDataPoints,
        labels: labels,
        chartType: "LineGradient",
        yLabel: title === "Engine Health" ? "Health Percent" : title,
        xLabel: "Time",
        isGradientOpposite: isGradientOpposite ?? false,
    };
}


const message_data = {
    0: { status: "Success", message: "All Ok" },
    1: { status: "Success", message: "Good Signal, No Synchronisation with TDC" },
    2: { status: "Success", message: "Good Signal, Synchronised with TDC" },
    3: { status: "Success", message: "Good Signal, Synchronised with CAM" },
    "-1": { status: "Fail", message: "Failed to apply configuration" },
    "-2": { status: "Fail", message: "Failed to load recorded signal" },
    "-3": { status: "Fail", message: "Unstable Speed." },
    "-4": {
        status: "Fail",
        message: "Too low speed. Check minRPM settings in Config",
    },
    "-5": { status: "Fail", message: "Too much speed variation." },
    "-6": { status: "Fail", message: "No Valid License" },
    "-7": { status: "Fail", message: "Unstable Speed." },
    "-8": { status: "Fail", message: "Signal Level - Too low" },
    "-10": { status: "Fail", message: "Speed Mismatch between Channels" },
    "-9": { status: "Fail", message: "Mismatch between config & channels info." },
    "-21": { status: "Fail", message: "1 Missing Teeth/ Junction defined as Standard" },
    "-22": { status: "Fail", message: "Multiple Junction defined as Standard" },
    "-23": { status: "Fail", message: "Multiple Junction defined as 1Junction/MissingTeeth" },
};

const torque_message_data = {
    0: { status: "Success", message: "All Ok" },
    1: { status: "Success", message: "Good Signal" },
    2: { status: "Success", message: "Good Signal" },
    3: { status: "Success", message: "Good Signal" },
    4: { status: "Success", message: "Good Signal" },
    5: { status: "Success", message: "Good Signal" },
    6: { status: "Success", message: "Good Signal" },
    "-1": { status: "Fail", message: "Failed to apply configuration" },
    "-2": { status: "Fail", message: "Failed to load recorded signal" },
    "-3": { status: "Fail", message: "Unstable Speed." },
    "-4": {
        status: "Fail",
        message: "Too low speed. Check minRPM settings in Config",
    },
    "-5": { status: "Fail", message: "Too much speed variation." },
    "-6": { status: "Fail", message: "No Valid License" },
    "-7": { status: "Fail", message: "Unstable Speed." },
    "-8": { status: "Fail", message: "Signal Level - Too low" },
    "-10": { status: "Fail", message: "Speed Mismatch between Channels" },
    "-9": { status: "Fail", message: "Mismatch between config & channels info." },
    "-21": { status: "Fail", message: "1 Missing Teeth/ Junction defined as Standard" },
    "-22": { status: "Fail", message: "Multiple Junction defined as Standard" },
    "-23": { status: "Fail", message: "Multiple Junction defined as 1Junction/MissingTeeth" },
};

function average(datapoints) {
    let sum = 0;
    for (let i of datapoints) {
        if (i) {
            sum = parseFloat(sum) + parseFloat(i);
        }
    }
    const avg = sum / datapoints.length;
    return parseFloat(avg).toFixed(2);
}

export function round(num) {
    return Math.round(num * 100) / 100;
}

function checkForAlert(dataPoints, min, max) {
    if (!dataPoints) return 0; //return no alert
    let isCompleteRed = false;
    let isCompletelyOrange = false;
    let isCompletelyGreen = false;

    for (let i of dataPoints) {
        if (i <= min) {
            isCompleteRed = true;
        } else if (i > min && i <= max) {
            isCompletelyOrange = true;
        } else if (i > max) {
            isCompletelyGreen = true;
        }
    }

    if (isCompletelyGreen) {
        return 0; //return no alert
    }

    if (isCompleteRed && !isCompletelyOrange) {
        return 1; //return error alert
    }

    if (isCompletelyOrange || isCompleteRed) {
        return 2;  //return warning alert
    }

    return 0; //return no alert
}

function buildTorqueAlertData(data) {
    let returnArray = [];

    if (data) {
        let tempCode = 999;
        for (let item of data) {
            const moduleData = JSON.parse(item["jsondata"]);
            if (!moduleData) {
                continue;
            }
            const code = moduleData["Status"];

            if (tempCode !== code) {
                const message = torque_message_data[code];
                if (message) {
                    returnArray.unshift({
                        instructionName: message["status"],
                        isTorque: true,
                        instructionType:
                            message["status"] === "Success" ? "success" : "error",
                        instructions: [
                            { message: message["message"], time: moduleData?.DateAndTime },
                        ],
                    });
                }
            }
            tempCode = code;
        }
    }

    return returnArray;
}

function buildBearingAlertData(data) {
    const mechanicalHealth = [];
    const bearing = [];

    const global_ = [];

    if (data) {
        for (const item of data) {
            const moduleData = JSON.parse(item["jsondata"]);

            const bearingGlobal = moduleData["2KMixed"];

            if (bearingGlobal) {
                mechanicalHealth.push(bearingGlobal["valueInHealth"]);
            }

            const bearingData = moduleData["BearingGlobal"];

            if (bearingData) {
                bearing.push(bearingData["valueInHealth"]);
            }

            const globalMixed = moduleData["GlobalMixed"];

            if (globalMixed) {
                global_.push(globalMixed["valueInHealth"]);
            }
        }
    }

    let returnArray = [];

    if (mechanicalHealth) {
        const val = average(mechanicalHealth);
        if (val <= 30) {
            returnArray.push({
                instructionName: "Shaft/Clearance",
                instructionType: "error",
                instructions: [
                    "Roller Bearing Case:",
                    "- Excessive Clearance in all type of bearings",
                    "- Excessive excentricity(static/dynamic) in the case of electric motors",
                    "- Excessive Tipping Displacement in case of vertical installations",
                    "- Excessive stresses causing the shaft to move with high displacement",
                    "- Excessive teeth mesh stresses in the case of a gearbox",

                    "Plain Bearing Case",
                    "- Clearance between bearing pad/bearing cap",
                    "- Excessive Excentricity (Static/dynamic) in the case of electric motors",
                    "- Excessive tipping displacement in the case of vertical installations",
                    "- Excessive teeth mesh stresses in the case of gearbox",
                ],
            });
        }
        if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Shaft/Clearance",
                instructionType: "warning",
                instructions: [
                    " Roller Bearing Case:",
                    "- Excessive Clearance in all type of bearings",
                    "- Excessive excentricity(static/dynamic) in the case of electric motors",
                    "- Excessive Tipping Displacement in case of vertical installations",
                    "- Excessive stresses causing the shaft to move with high displacement",
                    "- Excessive teeth mesh stresses in the case of a gearbox",

                    "Plain Bearing Case",
                    "- Clearance between bearing pad/bearing cap",
                    "- Excessive Excentricity (Static/dynamic) in the case of electric motors",
                    "- Excessive tipping displacement in the case of vertical installations",
                    "- Excessive teeth mesh stresses in the case of gearbox,",
                ],
            });
        }
    }
    if (bearing) {
        const val = average(bearing);
        if (val <= 30) {
            returnArray.push({
                instructionName: "Bearings",
                instructionType: "error",
                instructions: [
                    "Roller Bearing Case",
                    "- Incorrect alignment of the inner ring with the outer ring",
                    "- Race spill, axial sliding irregularities",
                    "- Insufficient axial preload in the case of preloaded bearing",
                    "- Irregularities of the rolling of bearing elements on the races due to excessive clearance related to underload in radial direction",
                    "- Damage of bearing element (Cage/elements/races)",
                    "- Wear of Pulleys/belts",
                    "- Defective Bearing between the bearing outer ring and bore",
                    "- Wear of Housing",

                    "Plain Bearing Case",
                    "- Instability due to axial excessive clearance in the case of thrust bearing radial in the case of guide bearing",

                    "Pads Bearing Case",
                    "- Pivot wear",
                    "- Difficulties of Pivoting pads(jamming)",
                ],
            });
        }
        if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Bearings",
                instructionType: "warning",
                instructions: [
                    "Roller Bearing Case",
                    "- Incorrect alignment of the inner ring with the outer ring",
                    "- Race spill, axial sliding irregularities",
                    "- Insufficient axial preload in the case of preloaded bearing",
                    "- Irregularities of the rolling of bearing elements on the races due to excessive clearance related to underload in radial direction",
                    "- Damage of bearing element (Cage/elements/races)",
                    "- Wear of Pulleys/belts",
                    "- Defective Bearing between the bearing outer ring and bore",
                    "- Wear of Housing",

                    "Plain Bearing Case",
                    "- Instability due to axial excessive clearance in the case of thrust bearing radial in the case of guide bearing",

                    "Pads Bearing Case",
                    "- Pivot wear",
                    "- Difficulties of Pivoting pads(jamming)",
                ],
            });
        }
    }

    if (global_) {
        const val = average(global_);

        if (val <= 30) {
            returnArray.push({
                instructionName: "Global(Imbalance/Alignment/Loosness)",
                instructionType: "error",
                instructions: [
                    "Roller Bearing without High Shock index",
                    "- Unbalance, Misalignment",
                    "- Cantilever Rotor Imbalance",
                    "- Mode (bending, Fitting) Excitation",

                    "Roller Bearing with High Shock Index",
                    "- Loosening/Clearance at the support structure",
                    "- Coupling Clearance, clamping wear(toothed Coupling), axial distance not repsected(coupling with flexible blades), cracking(flector)",

                    "Plain Bearings Case without high shock index",
                    "- Imbalance, Misalignment ",
                    "- Cantilever rotor imbalance",

                    "Plain Bearings with High Shock Index Value",
                    "- Coupling: Clearance, clamping wear(toothing Coupling), axial distance not respected (coupling with flexible blades), cracking(flector)",
                    "- Loosening of Caps and supports",
                ],
            });
        }

        if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Global(Imbalance/Alignment/Loosness)",
                instructionType: "warning",
                instructions: [
                    "Roller Bearing without High Shock index",
                    "- Unbalance, Misalignment",
                    "- Cantilever Rotor Imbalance",
                    "- Mode (bending, Fitting) Excitation",
                    "Roller Bearing with High Shock Index",
                    "- Loosening/Clearance at the support structure",
                    "- Coupling Clearance, clamping wear(toothed Coupling), axial distance not repsected(coupling with flexible blades), cracking(flector)",

                    "Plain Bearings Case without high shock index",
                    "- Imbalance, Misalignment",
                    "- Cantilever rotor imbalance",

                    "Plain Bearings with High Shock Index Value",
                    "- Coupling: Clearance, clamping wear(toothing Coupling), axial distance not respected (coupling with flexible blades), cracking(flector)",
                    "- Loosening of Caps and supports",
                ],
            });
        }
    }
    return returnArray;
}

function buildTurbochargerAlertData(data) {
    const shockIndex = [];
    const bearing = [];
    const unbalanceInstability = [];

    if (data) {
        for (const item of data) {
            const moduleData = JSON.parse(item["jsondata"]);

            const shockData = moduleData["GlobalKurto"];
            if (shockData && shockData["valueInPercent"] !== undefined && shockData["valueInPercent"] !== null) {
                shockIndex.push(Number(shockData["valueInPercent"]));
            }

            const bearingData = moduleData["4KMixed"];
            if (bearingData && bearingData["valueInPercent"] !== undefined && bearingData["valueInPercent"] !== null) {
                bearing.push(Number(bearingData["valueInPercent"]));
            }

            const unbalanceData = moduleData["GlobalMixed"];
            if (unbalanceData && unbalanceData["valueInPercent"] !== undefined && unbalanceData["valueInPercent"] !== null) {
                unbalanceInstability.push(Number(unbalanceData["valueInPercent"]));
            }
        }
    }

    const returnArray = [];

    const addPercentAlert = (values, instructionName, instructionLines) => {
        if (!values || values.length === 0) {
            return;
        }

        const val = average(values);
        if (val <= 30) {
            returnArray.push({
                instructionName,
                instructionType: "error",
                instructions: instructionLines,
            });
        } else if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName,
                instructionType: "warning",
                instructions: instructionLines,
            });
        }
    };

    addPercentAlert(shockIndex, "Shock index", [
        "Check for recurring impulsive events and transient shocks",
        "Inspect rotating group for looseness and local impacts",
        "Verify mounting rigidity and eliminate support play",
    ]);

    addPercentAlert(bearing, "Bearing", [
        "Check bearing wear, preload and lubrication quality",
        "Inspect raceways/rolling elements for local defects",
        "Verify shaft alignment and housing fit",
    ]);

    addPercentAlert(unbalanceInstability, "Unbalance Instability", [
        "Check mass unbalance and rotor alignment",
        "Inspect coupling/support looseness and clearance",
        "Verify structural stiffness around the support points",
    ]);

    return returnArray;
}

function buildMotorAlertData(data) {
    let returnArray = [];

    const electroMag = [];

    const bearing = [];

    const stressStability = [];

    if (data) {
        for (const item of data) {
            const moduleData = JSON.parse(item["jsondata"]);

            const mElectromag = moduleData["MElectromag"];

            if (mElectromag) {
                electroMag.push(mElectromag["valueInHealth"]);
            }

            const MBearing = moduleData["MBearing"];
            if (MBearing) {
                bearing.push(MBearing["valueInHealth"]);
            }

            const MStressStability = moduleData["MStressStability"];
            if (MStressStability) {
                stressStability.push(MStressStability["valueInHealth"]);
            }
        }
    }

    if (electroMag) {
        const val = average(electroMag);
        if (val <= 30) {
            returnArray.push({
                instructionName: "Electromagnetic Stress",

                instructionType: "error",

                instructions: [
                    "- Breakage or cracking of a short-circuit ring (also results in abnormal heating of the rotor at the point of cracking or rupture and an increase in current.)",
                ],
            });
        } else if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Electromagnetic Stress",

                instructionType: "warning",

                instructions: [
                    "- Check Insulation of winding",
                    "- Check mechanical strength of the windings in the slots (can also result in defective or even detached slot shims)",
                ],
            });
        }
    }

    if (bearing) {
        const val = average(bearing);

        if (val <= 30) {
            returnArray.push({
                instructionName: "Bearing",

                instructionType: "error",

                instructions: [
                    "Check for Air Gap in the B5 of an electric motor",
                    "Check for tilting in the case of vertical axis installations",
                    "Check for the degradation of bearings",
                    "Check support structure (assembly at the level of the supports)",
                ],
            });
        }

        if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Bearing",

                instructionType: "warning",

                instructions: [
                    "Check for Air Gap in the case of an electric motor",
                    "Check for tilting in the case of vertical axis installations",
                    "Check for the degradation of bearings",
                    "Check support structure (assembly at the level of the supports)",
                ],
            });
        }
    }

    if (stressStability) {
        const val = average(stressStability);

        if (val <= 30) {
            returnArray.push({
                instructionName: "Stress/Stability",

                instructionType: "error",

                instructions: [
                    "Check for Instability of the power supply (network / transformer / drive also) ",

                    "Check for Phase imbalance",

                    "Check for Mechanical overload ",

                    "Check for Uneven air gap (or off-center rotor in the stator), possibly also caused by a wobbly foot (or lame foot)",

                    "Check for Stator deformation (thermal or mechanical deformation)",

                    "In the case of a direct current motor, the possible cause may also be due to a falling armature insulation or a badly adjusted neutral line.",
                ],
            });
        }

        if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Stress/Stability",

                instructionType: "warning",

                instructions: [
                    "Check for Instability of the power supply (network / transformer / drive also) ",

                    "Check for Phase imbalance ",

                    "Check for Stator deformation (thermal or mechanical deformation)",

                    "In the case of a direct current motor, the possible cause may also be due to a falling armature insulation or a badly adjusted neutral line.",
                ],
            });
        }
    }

    return returnArray;
}

function buildTurbineAlertData(data) {
    let returnArray = [];

    const regularityDeviation = [];

    const bearingStatus = [];

    const bladeStatus = [];

    const turbineCoupling = [];
    const combustionCondition = [];

    if (data) {
        for (const item of data) {
            const moduleData = JSON.parse(item["jsondata"]);
            const RegularityDeviation = moduleData["RegularityDeviation"];

            if (RegularityDeviation) {
                regularityDeviation.push(RegularityDeviation["valueInHealth"]);
            }

            const BearingStatus = moduleData["BearingStatus"];

            if (BearingStatus) {
                bearingStatus.push(BearingStatus["valueInHealth"]);
            }

            const combustion = moduleData["CombustionKit"];

            if (combustion) {
                combustionCondition.push(combustion["valueInHealth"]);
            }

            const BladeStatus = moduleData["BladeStatus"];

            if (BladeStatus) {
                bladeStatus.push(BladeStatus["valueInHealth"]);
            }

            const TurbineCoupling = moduleData["TurbineCoupling"];
            if (TurbineCoupling) {
                turbineCoupling.push(TurbineCoupling["valueInHealth"]);
            }
        }
    }

    if (turbineCoupling) {
        const val = average(turbineCoupling);

        if (val <= 30) {
            returnArray.push({
                instructionName: "Coupling/Alignment",
                instructionType: "error",
                instructions: [
                    "Inspection of coupling condition for Fatigue",
                    "Checking alignment factors between the turbine and the coupled element",
                ],
            });
        } else if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Coupling/Alignment",
                instructionType: "warning",
                instructions: [
                    "Inspection of coupling condition for Fatigue",
                    "Checking alignment factors between the turbine and the coupled element",
                ],
            });
        }
    }
    if (combustionCondition) {
        const val = average(combustionCondition);

        if (val <= 30) {
            returnArray.push({
                instructionName: "Combustion Kit",
                instructionType: "error",
                instructions: ["Defective injection system"],
            });
        }
    }

    if (bladeStatus) {
        const val = average(bladeStatus);

        if (val <= 30) {
            returnArray.push({
                instructionName: "Shaft/Blades Health",

                instructionType: "error",

                instructions: [
                    "Check for clamping or dynamic instabilities or damage to rotating/fixed blades",
                    "Check for Non-linearities in the assembly of the connecting structure to the base/ground",
                ],
            });
        }

        if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Shaft/Blades Health",

                instructionType: "warning",

                instructions: [
                    "Check for clamping, dynamic instabilities, damage to rotating blades",
                    "Check for Non-linearities in the assembly of the connecting structure to the base/ground",
                ],
            });
        }
    }

    if (bearingStatus) {
        const val = average(bearingStatus);

        if (val <= 30) {
            returnArray.push({
                instructionName: "Bearing Status",

                instructionType: "error",

                instructions: [
                    "Check Cylinder defect (bore/bearing shell)",

                    "Check for structure/support bending",

                    "Check for the excessive dynamic gearmesh force  ",

                    "Check for misalignment see Coupling indicator",
                    "Check axial stability between the thrust Bearings",
                    "Check dynamic seal defects",
                    "Check Stator geometry defect",
                ],
            });
        }

        if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Bearing Status",

                instructionType: "warning",

                instructions: [
                    "Check Cylinder defect (bore/bearing shell)",

                    "Check for structure/support bending",

                    "Check for the excessive dynamic gearmesh force  ",

                    "Check for misalignment see Coupling indicator",
                    "Check axial stability between the thrust Bearings",
                    "Check dynamic seal defects",
                    "Check Stator geometry defect",
                ],
            });
        }
    }

    if (regularityDeviation) {
        const val = average(regularityDeviation);
        if (val <= 30) {
            returnArray.push({
                instructionName: "Regularity/Deviation",
                instructionType: "error",
                instructions: [
                    "Check for the Faults in Steam Intake",
                    "Check the lubrication of the bearings and thrust bearings",
                ],
            });
        } else if (val > 30 && val <= 70) {
            returnArray.push({
                instructionName: "Regularity/Deviation",
                instructionType: "warning",
                instructions: [
                    "Check for the Faults in Steam Intake",
                    "Check the lubrication of the bearings and thrust bearings",
                ],
            });
        }
    }

    return returnArray;
}

function buildPropellerAlertData(data) {
    let returnArray = [];

    const propellerEfficiency = [];

    const propellerUnbalance = [];

    const propellerBearing = [];

    const propellerFouling = [];

    if (data) {
        for (const item of data) {
            const moduleData = JSON.parse(item["jsondata"]);
            const PropellerEfficiency = moduleData["PropellerEfficiency"];

            if (PropellerEfficiency) {
                propellerEfficiency.push(PropellerEfficiency["value"]);
            }

            const PropellerUnbalance = moduleData["PropellerUnbalance"];

            if (PropellerUnbalance) {
                propellerUnbalance.push(PropellerUnbalance["valueInPercent"]);
            }

            const PropellerBearing = moduleData["PropellerBearing"];

            if (PropellerBearing) {
                propellerBearing.push(PropellerBearing["valueInHealth"]);
            }

            const PropellerFouling = moduleData["PropellerFouling"] || moduleData["PropellerFooling"];

            if (PropellerFouling) {
                propellerFouling.push(PropellerFouling["value"]);
            }
        }
    }

    // if (propellerEfficiency) {
    //     const val = average(propellerEfficiency);
    //     if (val > 3.5 && val <= 5) {
    //         returnArray.push({
    //             instructionName: "Propeller Efficiency",
    //             instructionType: "error",
    //             instructions: [
    //                 "Inspect for marine growth or debris on blades.",
    //                 "Verify blade pitch angle. Check for hull fouling or increased resistance.",
    //                 "High efficiency drop requires cleaning or propeller tuning",
    //             ],
    //         });
    //     } else if (val > 2.5 && val <= 3.5) {
    //         returnArray.push({
    //             instructionName: "Propeller Efficiency",
    //             instructionType: "warning",
    //             instructions: [
    //                 "Inspect for marine growth or debris on blades.",
    //                 "Verify blade pitch angle. Check for hull fouling or increased resistance.",
    //                 "High efficiency drop requires cleaning or propeller tuning",
    //             ],
    //         });
    //     }
    // }

    if (propellerUnbalance) {
        const val = average(propellerUnbalance);
        if (val > 75) {
            returnArray.push({
                instructionName: "Propeller Unbalance",
                instructionType: "error",
                instructions: [
                    "Inspect for damaged, eroded, or sediment-laden blades.",
                    "Remove fouling and sediment.",
                    "If cavitation marks or blade deformation are found, schedule repairs immediately to restore balance.",
                ],
            });
        } else if (val > 30 && val <= 75) {
            returnArray.push({
                instructionName: "Propeller Unbalance",
                instructionType: "warning",
                instructions: [
                    "Inspect for damaged, eroded, or sediment-laden blades.",
                    "Remove fouling and sediment.",
                    "If cavitation marks or blade deformation are found, schedule repairs immediately to restore balance.",
                ],
            });
        }
    }

    // if (propellerBearing) {
    //     const val = average(propellerBearing);
    //     if (val <= 30) {
    //         returnArray.push({
    //             instructionName: "Stern Tube Bearing",
    //             instructionType: "error",
    //             instructions: [
    //                 "Check lubrication level and quality, inspect for wear or overheating, check for shaft alignment.",
    //                 "Replace bearings if necessary.",
    //             ],
    //         });
    //     } else if (val > 30 && val <= 70) {
    //         returnArray.push({
    //             instructionName: "Stern Tube Bearing",
    //             instructionType: "warning",
    //             instructions: [
    //                 "Check lubrication level and quality, inspect for wear or overheating, check for shaft alignment.",
    //                 "Replace bearings if necessary.",
    //             ],
    //         });
    //     }
    // }

    if (propellerFouling) {
        const val = average(propellerFouling);
        if (val > 0.75) {
            returnArray.push({
                instructionName: "Propeller Fouling",
                instructionType: "error",
                instructions: [
                    "Inspect for marine growth or debris on blades.",
                    // "Verify blade pitch angle. Check for hull fouling or increased resistance.",
                    // "High efficiency drop requires cleaning or propeller tuning",
                ],
            });
        } 
        // else if (val < 0.75 && val >= 0.25) {
        //     returnArray.push({
        //         instructionName: "Propeller Fouling",
        //         instructionType: "warning",
        //         instructions: [
        //             "Inspect for marine growth or debris on blades.",
        //             "Verify blade pitch angle. Check for hull fouling or increased resistance.",
        //             "High efficiency drop requires cleaning or propeller tuning",
        //         ],
        //     });
        // }
    }
    return returnArray;
}

function buildEngineAlertData(historical_data) {
    let returnArray = [];

    if (!historical_data || !historical_data[0]) {
        return returnArray;
    }

    const buildData = {
        engineHealth: [],
        engineEfficiency: [],
        powerLoss: [],
        Damper: [],
        Unbalance: [],
        CamPump: [],
    };
    for (let item of historical_data) {
        const data = JSON.parse(item["jsondata"]);

        if (data["MechanicalHealth"]) {
            buildData.engineHealth.push(data["MechanicalHealth"]["valueInHealth"]);
        }
        if (data["EngineEfficiency"]) {
            buildData.engineEfficiency.push(
                data["EngineEfficiency"]["valueInHealth"]
            );
        }
        if (data["PowerLoss"]) {
            buildData.powerLoss.push(data["PowerLoss"]["value"]);
        }
        if (data["Damper"]) {
            buildData.Damper.push(data["Damper"]["valueInHealth"]);
        }
        if (data["Unbalance"]) {
            buildData.Unbalance.push(data["Unbalance"]["valueInHealth"]);
        }
        // if (data["CamPump"]) {
        //     buildData.CamPump.push(data["CamPump"]["valueInHealth"]);
        // }
    }

    let conditionValue = checkForAlert(buildData.CamPump, 30, 70);
    if (conditionValue === 2) {
        returnArray.push({
            instructionName: "Governing and Crank driven Access",
            instructionType: "warning",
            instructions: [
                "If engine has just been overhauled, check for faulty assembly",
                "Check governor/control system functioning.",
                "Check gear condition/Backlash",
                "Check Irregularity in fuel regulation system.",
                "Check condition of attached pumps",
                "Check condition of Camshaft/rollers/followers",
                "Check camshaft damper if fitted",
                "Check for water in fuel if engine is hunting",
            ],
        });
    } else if (conditionValue === 1) {
        returnArray.push({
            instructionName: "Governing and Crank driven Access",
            instructionType: "error",
            instructions: [
                "Initiate corrective action immediately to improve health of Governing and crank driven accessories",
                "If engine has just been overhauled, check for faulty assembly",
                "Check governor/control system functioning.",
                "Check gear condition/Backlash",
                "Check Irregularity in fuel regulation system.",
                "Check condition of attached pumps",
                "Check condition of Camshaft/rollers/followers",
                "Check camshaft damper if fitted",
                "Check for water in fuel if engine is hunting",
            ],
        });
    }

    conditionValue = checkForAlert(buildData.Unbalance, 30, 70);
    if (conditionValue === 2) {
        returnArray.push({
            instructionName: "Performance of Mounts & Supports",
            instructionType: "warning",
            instructions: [
                "Check the tightness of Support Structure assemblies",
                "Check the tightness of Mounts",
                "Check the condition of Anti Vibration Pads",
            ],
        });
    } else if (conditionValue === 1) {
        returnArray.push({
            instructionName: "Performance of Mounts & Supports",
            instructionType: "error",
            instructions: [
                "Take Immediate Action on Mounts",
                "Check the tightness of Support Structure assemblies",
                "Check the tightness of Mounts",
                "Replace Anti Vibration pads if due for overhaul",
            ],
        });
    }

    conditionValue = checkForAlert(buildData.Damper, 30, 70);
    if (conditionValue === 2) {
        returnArray.push({
            instructionName: "Performance of Vibration Damper",
            instructionType: "warning",
            instructions: [
                "Check the activity of the torisonal vibration damper at specific frequencies-different speeds",
            ],
        });
    } else if (conditionValue === 1) {
        returnArray.push({
            instructionName: "Performance of Vibration Damper",
            instructionType: "error",
            instructions: [
                "Take Immediate action to improve the health of Damper by Checking the activity of the torisonal vibration damper ",
            ],
        });
    }

    conditionValue = checkForAlert(buildData.powerLoss, 1.9, 3);
    if (conditionValue === 2) {
        returnArray.push({
            instructionName: "Increase in Fuel Consumption",
            instructionType: "warning",
            instructions: [
                "Check engine running hours",
                "Check individual cylinder indicators",
                "Check fuel quality",
            ],
        });
    }

    conditionValue = checkForAlert(buildData.engineEfficiency, 30, 70);
    if (conditionValue === 2) {
        returnArray.push({
            instructionName: "Combustion Condition",
            instructionType: "warning",
            instructions: [
                "Check Compression and Injection indicators on specific cylinders and action accordingly. ",
            ],
        });
    } else if (conditionValue === 1) {
        returnArray.push({
            instructionName: "Combustion Condition",
            instructionType: "error",
            instructions: [
                "Initiate immediate action to improve operation condition by Checking Compression and Injection indicators on specific cylinders",
            ],
        });
    }
    conditionValue = checkForAlert(buildData.engineHealth, 30, 70);
    if (conditionValue === 2) {
        returnArray.push({
            instructionName: "Engine Health",
            instructionType: "warning",
            instructions: [
                "Check other mechanical global indicators and cylinder specific indicators and take action accordingly",
            ],
        });
    } else if (conditionValue === 1) {
        returnArray.push({
            instructionName: "Engine Health",
            instructionType: "error",
            instructions: [
                "Initiate action to improve mechanical health immediately after checking other global and cylinder specific mechanical indicators.",
            ],
        });
    }

    const item = historical_data.at(-1);

    const data = JSON.parse(item["jsondata"]);

    const compression = data["Compression"];

    const bearing = data["Bearing"];

    const bearingBis = data["BearingBis"];

    const misfiring = data["Misfiring"];

    const InjectionCondition = data["InjectionCondition"]; //Performance of Vibration Damper

    const min = 30;
    const max = 70;

    if (InjectionCondition) {
        let isCompleteRed = false;
        let isCompletelyOrange = false;

        for (const i of InjectionCondition?.cylinderHealth) {
            if (i <= min) {
                isCompleteRed = true;
            } else if (i > min && i <= max) {
                isCompletelyOrange = true;
            }
        }
        if (isCompleteRed && !isCompletelyOrange) {
            returnArray.push({
                instructionName: "Fuel Injection Performance",
                instructionType: "error",
                instructions: [
                    "Inititate immediate Corrective action to improve Fuel Injection Performance",
                    "If fuel system has just been overhauled, check for faulty assembly",
                    "Check running hours if injector/pump is due for overhaul",
                    "Check Fuel oil filter/filter pressure. Check for fuel quality",
                    "Check for injector timing.",
                    "Pressure test injector/Dismantle fuel pump/injector. Overhaul if neccessary.",
                    "Check fuel linkage in affected cylinders",
                ],
            });
        } else if (isCompletelyOrange || isCompleteRed) {
            returnArray.push({
                instructionName: "Fuel Injection Performance",
                instructionType: "warning",
                instructions: [
                    "If fuel system has just been overhauled, check for faulty assembly",
                    "Check running hours if injector/pump is due for overhaul",
                    "Check Fuel oil filter/filter pressure. Check for fuel quality",
                    "Check for injector timing.",
                    "Pressure test injector/Dismantle fuel pump/injector. Overhaul if neccessary. ",
                    "Check fuel linkage in affected cylinders",
                ],
            });
            /*} else if (waring) {
                returnArray.push({
                    instructionName: "Fuel Injection Performance",
                    instructionType: "warning",
                    instructions: [
                        "If fuel system has just been overhauled, check for faulty assembly",
                        "Check running hours if injector/pump is due for overhaul",
                        "Pressure test injector/Dismantle fuel pump/injector. Overhaul if neccessary.",
                        "In case of electronic injectors, check solenoid valves/timing",
                        "Check fuel linkage in affected cylinders",
                    ],
                });
            }*/
        }
    }
    if (compression) {
        let isCompleteRed = false;
        let isCompletelyOrange = false;

        for (const i of compression?.cylinderHealth) {
            if (i <= min) {
                isCompleteRed = true;
            } else if (i > min && i <= max) {
                isCompletelyOrange = true;
            }
        }
        if (isCompleteRed && !isCompletelyOrange) {
            returnArray.push({
                instructionName: "Compression in Cylinders",
                instructionType: "error",
                instructions: [
                    "If top overhaul has just been performed, check for faulty assembly",
                    "Check for engine running hours if due for overhaul",
                    "Check Crankcase pressure which may indicate liner/piston issues.",
                    "Check Compression Pressure of specific cylinder",
                    "Check Exhaust Temperatures specific cylinder which may indicate valve tighntess issues",
                    "Use Borescope for further identification",
                    "Check for deterioration in the Condition of Cyl Moving Parts",
                    "Check for blue exhaust smoke indicating broken piston ring and excessive lube being burnt",
                ],
            });
        } else if (isCompletelyOrange || isCompleteRed) {
            returnArray.push({
                instructionName: "Compression in Cylinders",
                instructionType: "warning",
                instructions: [
                    "If top overhaul has just been performed, check for faulty assembly",
                    "Check Tappet Settings",
                    "Check Crankcase pressure which may indicate liner/piston issues.",
                    "Check Compression Pressure of specific cylinder",
                    "Check Exhaust Temperatures specific cylinder which may indicate valve tighntess issues",
                    "Use Borescope for further identification",
                    "Check for deterioration in the Condition of Cyl Moving Parts",
                    "Check for blue exhaust smoke indicating broken piston ring and excessive lube being burnt",
                ],
            });
            /*  } else if (waring) {
                  returnArray.push({
                      instructionName: "Compression in Cylinders",
                      instructionType: "warning",
                      instructions: [
                          "If top overhaul has just been performed, check for faulty assembly",
                          "Check Tappet Settings",
                          "Check Crankcase pressure which may indicate liner/piston issues.",
                          "Check Compression Pressure of specific cylinder",
                          "Check Exhaust Temperatures specific cylinder which may indicate valve tighntess issues",
                          "Use Borescope for further identification",
                          "Check for deterioration in the Condition of Cyl Moving Parts",
                          "Check for engine running hours if due for overhaul",
                          "Check for blue exhaust smoke indicating broken piston ring and excessive lube being burnt",
                      ],
                  });
              }*/
        }
    }
    if (bearing) {

        let isCompleteRed = false;
        let isCompletelyOrange = false;

        for (const i of bearing?.cylinderHealth) {
            if (i <= min) {
                isCompleteRed = true;
            } else if (i > min && i <= max) {
                isCompletelyOrange = true;
            }
        }
        if (isCompleteRed && !isCompletelyOrange) {
            returnArray.push({
                instructionName: "Bearing Condition",
                instructionType: "error",
                instructions: [
                    "If engine has just been overhauled, check for faulty assembly",
                    "Check if the engine is due for overhaul",
                    "Take immediate corrective action on Bearings",
                    "Identify defect in the Main or Bottom end bearing",
                    "Check bearing clearances",
                    "Incase of bottom end bearing, check free movement of rod.",
                    "Check if bearing shelves have moved from their original position",
                    "Incase of excessive clearances of bearings, check for bearing damage.",
                    "Check for Peak Pressure of specific cylinders",
                    "Check for Clogged Oil Passage of Individual Bearing",
                    "Check tightness of bearing bolts",
                ],
            });
        } else if (isCompletelyOrange || isCompleteRed) {
            returnArray.push({
                instructionName: "Bearing Condition",
                instructionType: "warning",
                instructions: [
                    "If engine has just been overhauled, check for faulty assembly",
                    "Check if the bearings are due for overhaul",
                    "Check external influence on crankshaft from the driven load",
                    "Check bearing clearances/Damages",
                    "Check Damper indicator to detect malfunction",
                    "Check Lube Oil Pressure.",
                    "Check oil contamination for water/fuel",
                    "Check Lube oil filter for wear debris",
                    "Check crankshaft Misalignment",
                ],
            });
        }/* else if (waring) {
            returnArray.push({
                instructionName: "Bearing Condition",
                instructionType: "warning",
                instructions: [
                    "If engine has just been overhauled, check for faulty assembly",
                    "Identify defect in the Main or Bottom end bearing",
                    "Check bearing clearances",
                    "In case of bottom end bearing, check free movement of rod.",
                    "Check if bearing shelves have moved from their original position",
                    "In case of excessive clearances of bearings, check for bearing damage.",
                    "Check for Peak Pressure of specific cylinders",
                    "Check for Clogged Oil Passage of Individual Bearing",
                    "Check tightness of bearing bolts",
                ],
            });
        }*/

    }
    if (bearingBis) {
        let isCompleteRed = false;
        let isCompletelyOrange = false;

        for (const i of bearingBis?.cylinderHealth) {
            if (i <= min) {
                isCompleteRed = true;
            } else if (i > min && i <= max) {
                isCompletelyOrange = true;
            }
        }
        if (isCompleteRed && !isCompletelyOrange) {
            returnArray.push({
                instructionName: "Condition of Cyl Moving Parts",
                instructionType: "error",
                instructions: [
                    "Take Immediate corrective action to improve Condition of CYl moving parts",
                    "If engine has just been overhauled, check for faulty assembly",
                    "Check engine running hours to identify if the engine is due for overhaul.",
                    "Carry out borescope inspection through cylinder head of piston top for carbon deposits. Eliminate bad fuel injector",
                    "Check quality of fuel oil to eliminate possibility of scuffing of cylinder liners",
                    "Check Jacket cooling water temperature",
                    "Check Lube oil filter for debris",
                    "Check for colour of exhaust(blue smoke/black smoke)",
                    "Open crankcase, check liner surface of affected cylinders through borescope inspection if possible",
                ],
            });
        } else if (isCompletelyOrange || isCompleteRed) {
            returnArray.push({
                instructionName: "Condition of Cyl Moving Parts",
                instructionType: "warning",
                instructions: [
                    "If engine has just been overhauled, check for faulty assembly",
                    "Check engine running hours to identify if the engine is due for overhaul.",
                    "Carry out borescope inspection through cylinder head of piston top for carbon deposits. Eliminate bad fuel injector",
                    "Check quality of fuel oil to eliminate possibility of scuffing of cylinder liners",
                    "Check Jacket cooling water temperature",
                    "Check Lube oil filter for debris",
                    "Check for colour of exhaust(blue smoke/black smoke)",
                    "Open crankcase, check liner surface of affected cylinders through borescope inspection if possible",
                ],
            });
            /*} else if (waring) {
                returnArray.push({
                    instructionName: "Condition of Cyl Moving Parts",
                    instructionType: "warning",
                    instructions: [
                        "If engine has just been overhauled, check for faulty assembly",
                        "Check if the engine is due for overhaul",
                        "Check Jacket water cooling temperature of affected cylinders",
                        "Check for Piston cooling nozzle alignment or clogging of affected cylinder",
                        "Check for Broken piston Ring of affected cylinder",
                        "Check for colour of exhaust. Blue Colour may indicate excessive lube burning, black may indicate incomplete combustion.",
                        "Check liner surface of affected cylinders from crankcase through borescope inspection if possible",
                    ],
                });
            }*/
        }
    }
    if (misfiring) {
        let isCompleteRed = true;
        let isCompletelyOrange

        for (const i of misfiring?.cylinderHealth) {
            if (i > 0 && i < min) {
                isCompleteRed = true;
                break;
            }
        }

        if (!isCompleteRed) {
            returnArray.push({
                instructionName: "Misfiring",
                instructionType: "error",
                instructions: [
                    "CAUTION - Cylinder Misfired. Please check Cylinder Specific Indicators below",
                    "Check if knocking sensors are activated/Malfunctioning",
                    "Check Gas quality if multiple units are knocking. Reduce engine load.",
                    "Check Spark Plug condition/Malfunctioning",
                    "Check engine timing.",
                    "Check Spark plug Cable, Ignition coil.",
                    "Check Turbocharger' air filters",
                ],
            });
        }
    }

    return returnArray;
}

function buildLineChart(data, key, title, isGradientOpposite, hideBackground, maxPower) {
    const isTorqueChart = title === "Torque with RPM" || title === "Power with RPM";
    let yMaxValue = maxPower || 6;
    let labels = [];
    let datapoints = [];
    let zAxisDataPoints = [];
    let count = 0;
    let hasAuxPowerPercent = false; // Flag to check presence of AUX.PowerPercent.Value
    if (data) {
        for (let item of data) {
            const moduleData = JSON.parse(item["jsondata"]);

            if (moduleData?.Status <= 0 || !moduleData?.DateAndTime) {
                continue;
            }
            labels.push(getdate(moduleData?.DateAndTime));

            const auxPowerPercent =
                Object.keys(moduleData)
                    .filter(k => k.startsWith("Aux"))
                    .map(k => parseInt(moduleData[k]?.PowerPercent?.Value, 10))
                    .find(v => !isNaN(v) && v > 0) || 0;

            const channelSpeed = parseInt(moduleData.ChannelSpeed || 0);

            if (auxPowerPercent && !isTorqueChart) {
                zAxisDataPoints.push(auxPowerPercent);
                hasAuxPowerPercent = true; // Set flag if AUX.PowerPercent.Value is present
            } else {
                zAxisDataPoints.push(channelSpeed);
            }

            try {
                const valueObject = moduleData[key];
                const rawValue = valueObject?.value ?? valueObject?.Value ?? 0;
                datapoints.push(round(Number(rawValue) * 0.001));
            } catch (e) {
                console.error("ignorable error", e);
            }
            count++;
        }
    }
    let sum = 0;
    for (let i of datapoints) {
        sum += parseInt(i, 10); //don't forget to add the base
    }

    let avg = sum / datapoints.length;
    const validDatapoints = datapoints.filter((value) => Number.isFinite(value));
    const dataMax = validDatapoints.length > 0 ? Math.max(...validDatapoints) : 0;
    const chartYMax = isTorqueChart
        ? Math.max(dataMax * 1.1, 1)
        : yMaxValue;

    return {
        trendsName: title,
        /* Rohit Updated 6 Jun*/
        speedName: isTorqueChart ? "RPM" : (hasAuxPowerPercent ? "Power" : "Speed"), // Torque charts always use RPM on the secondary axis.
        min: round(Math.min(...datapoints)),
        max: round(Math.max(...datapoints)),
        yMax: title === "Engine Health" ? 110 : chartYMax,
        yMin: isTorqueChart && dataMax === 0 ? -1 : 0,
        avg: round(avg),
        datapoints: datapoints,
        dataPointsY1: zAxisDataPoints,
        labels: labels,
        chartType: "LineGradient",
        yLabel: title === "Engine Health" ? "Health Percent" : title,
        xLabel: "Time",
        hideBackground: hideBackground ?? false,
        isGradientOpposite: isGradientOpposite ?? false,
    };
}


function calculateTorqueValue(value, key) {
    
    if (value) {
        if (key === "StaticTorque") {
            return parseFloat((value || 0) * 0.001).toFixed(2);
        }

        if (key === "StaticPower") {
            return parseFloat((value || 0) * 0.001).toFixed(2);
        }
        return parseFloat(value).toFixed(2);
    } else {
        return 0;
    }
}

/**
 * The `buildTrendData` function takes in historical data, a type, and additional data, and builds a
 * trend data set based on the input.
 * @param historical_data - The `historical_data` parameter is an array of objects that contains
 * historical data. Each object in the array represents a data entry and has a property called
 * "jsondata" which contains the data in JSON format.
 * @param type - The `type` parameter is a string that specifies the type of trend data to build. It
 * could be "Engine" or any other value depending on the specific requirements of your application.
 * @param from_data - The `from_data` parameter is a JSON string that contains additional data needed
 * for building the trend data. It is parsed into an object using `JSON.parse()` in the code.
 */
export function buildTrendData(historical_data, type, from_data) {
    let labels = [];
    const rpmData = [];
    const resultSet = [];
    from_data = JSON.parse(from_data);
    const firingOrderLabel = from_data?.firing_order
        ? from_data?.firing_order.trim().split(",")
        : [];

    for (const itemData of historical_data) {
        const item = JSON.parse(itemData["jsondata"]);

        if (item.Status <= 0 || !item?.DateAndTime) {
            continue;
        }

        let toPush = false;
        /* The above code is checking if the value of the variable "type" is equal to "Engine". If it
        is, it retrieves the firing order from the "item" object. */
        // 🔥 Handle all AUX dynamically (Aux1, Aux2, Aux3...)
        for (const itemKey in item) {
            if (!itemKey.startsWith("Aux")) continue;

            const auxData = item[itemKey];
            if (!auxData) continue;

            for (const paramKey in auxData) {
                const paramObj = auxData[paramKey];
                if (!paramObj?.Value) continue;

                // Example labels:
                // Aux1_PowerPercent
                // Aux2_Temperature
                const label = `${itemKey}_${paramKey}`;
                const value = parseFloat(paramObj.Value);

                const foundIndex = resultSet.findIndex(x => x.key === label);
                if (foundIndex !== -1) {
                    resultSet[foundIndex].data.push(value);
                } else {
                    resultSet.push({
                        key: label,
                        data: [value],
                    });
                }

                toPush = true;
            }
        }

        if (type === "Engine") {
            const firingOrder = item?.Engine.FiringOrder;

            for (const key in item) {
                if (keysToIgnore[type].includes(key)) {
                    const objData = item[key];

                    // -- Uncommented/Legacy Logic for Pressure --
                    /*
                    if (key === "Pressure") {
                        const pressureValue = objData.value;
                        if (objData && objData.hasOwnProperty("cylinderValues")) {
                            const pressureValues = objData.cylinderValues;
                            const pressureLabel = "Pressure";
                            const foundPressureIndex = resultSet.findIndex((x) => x.key === pressureLabel);

                            if (foundPressureIndex !== -1) {
                                resultSet[foundPressureIndex].data.push(pressureValue);
                            } else {
                                if (pressureValue > 0) {
                                    resultSet.push({
                                        key: pressureLabel,
                                        data: [pressureValue],
                                    });
                                }
                            }
                            for (let i = 0; i < pressureValues.length; i++) {
                                const label = `Pressure Cyl ${firingOrderLabel[i]}`;
                                const pressureValue = pressureValues[i];

                                const foundIndex = resultSet.findIndex((x) => x.key === label);
                                if (foundIndex !== -1) {
                                    resultSet[foundIndex].data.push(pressureValue);
                                } else {
                                    resultSet.push({
                                        key: label,
                                        data: [pressureValue],
                                    });
                                }
                            }
                        }
                    }
                    */

                    // -- Uncommented/Legacy Logic for Cylinder Health --
                    /*
                    else if (objData.hasOwnProperty("cylinderHealth") && objData?.cylinderHealth) {
                        const cylArray = objData?.cylinderHealth;
                        let i = 0;
                        for (let order of firingOrder) {
                            if (firingOrderLabel[i]) {
                                let label;

                                if (key === "Compression") {
                                    label = `Compression Condition Cyl ${firingOrderLabel[i]}`;
                                } else if (key === "Bearing") {
                                    label = `Bearing Condition Cyl ${firingOrderLabel[i]}`;
                                } else if (key === "BearingBis") {
                                    label = `Condition of Cyl Moving Parts Cyl ${firingOrderLabel[i]}`;
                                } else if (key === "InjectionCondition") {
                                    label = `Injection Condition ${firingOrderLabel[i]}`;
                                } else {
                                    label = `${key} Cyl ${firingOrderLabel[i]}`;
                                }

                                const foundIndex = resultSet.findIndex(
                                    (x) => x.key === label
                                );
                                if (foundIndex !== -1) {
                                    resultSet[foundIndex].data.push(cylArray[i] ? cylArray[i] : 0);
                                } else {
                                    resultSet.push({
                                        key: label,
                                        data: [cylArray[i] ? cylArray[i] : 0],
                                    });
                                }
                            }
                            i++;
                        }
                    }
                    */

                    if (key === "Pressure") {
                        // Ensure only when Status === 3
                        if (item.Status === 3) {
                            if (
                                objData &&
                                objData.hasOwnProperty("cylinderValues")
                            ) {
                                const pressureValues = objData.cylinderValues;
                                if (pressureValues && pressureValues.length > 0) {
                                    for (let i = 0; i < pressureValues.length; i++) {
                                        const pressureValue = pressureValues[i];
                                        if (pressureValue !== 0) {
                                            const label = `Pressure Cyl ${firingOrderLabel[i]}`;
                                            const foundIndex = resultSet.findIndex(
                                                (x) => x.key === label
                                            );
                                            if (foundIndex !== -1) {
                                                resultSet[foundIndex].data.push(
                                                    pressureValue
                                                );
                                            } else {
                                                resultSet.push({
                                                    key: label,
                                                    data: [pressureValue],
                                                });
                                            }
                                        }
                                    }
                                }
                            }

                            // Add this block if you also want to show general 'Pressure' value when status == 3
                            if (objData?.value && objData?.value > 0) {
                                const label = "Pressure";
                                const foundIndex = resultSet.findIndex(
                                    (x) => x.key === label
                                );
                                if (foundIndex !== -1) {
                                    resultSet[foundIndex].data.push(objData.value);
                                } else {
                                    resultSet.push({
                                        key: label,
                                        data: [objData.value],
                                    });
                                }
                            }
                        }
                    } else if (
                        objData.hasOwnProperty("cylinderHealth") &&
                        objData?.cylinderHealth
                    ) {
                        const cylArray = objData?.cylinderHealth;
                        let i = 0;
                        for (let order of firingOrder) {
                            if (firingOrderLabel[i]) {
                                let label;

                                if (key === "Compression") {
                                    label = `Compression Condition Cyl ${firingOrderLabel[i]}`;
                                } else if (key === "Bearing") {
                                    label = `Bearing Condition Cyl ${firingOrderLabel[i]}`;
                                } else if (key === "BearingBis") {
                                    label = `Condition of Cyl Moving Parts Cyl ${firingOrderLabel[i]}`;
                                } else if (key === "InjectionCondition") {
                                    label = `Injection Condition ${firingOrderLabel[i]}`;
                                } else {
                                    label = `${key} Cyl ${firingOrderLabel[i]}`;
                                }

                                const value = cylArray[i] || 0;
                                console.log("Value:", value);
                                if (value !== 0) {
                                    console.log("Label:", label);
                                    const foundIndex = resultSet.findIndex(
                                        (x) => x.key === label
                                    );
                                    if (foundIndex !== -1) {
                                        resultSet[foundIndex].data.push(value);
                                    } else {
                                        resultSet.push({
                                            key: label,
                                            data: [value],
                                        });
                                    }
                                } else {
                                    console.log("Skipping value:", value);
                                }
                            }
                            i++;
                        }
                    }

                    const trendKey = trendTitle[type][key] || toTitleCase(key);
                    const useRawValue = ["PropellerEfficiency", "Drop in Propeller Efficiency", "Twist Angle"].includes(trendKey);

                    const foundIndex = resultSet.findIndex((x) => x.key === key);
                    let valueToUse;
                    if (key === "PowerLoss") {
                        valueToUse = objData?.value ? parseFloat(objData?.value).toFixed(2) : 0;
                    } else if (useRawValue) {
                        valueToUse = objData?.value ? parseFloat(objData?.value) : 0;
                    } else {
                        valueToUse = objData?.valueInHealth ? objData?.valueInHealth : 0;
                    }

                    if (foundIndex !== -1) {
                        resultSet[foundIndex].data.push(valueToUse);
                    } else {
                        resultSet.push({
                            key,
                            data: [valueToUse],
                        });
                    }

                    toPush = true;
                }
            }
        } else if (type === "Torque") {
            for (const key in item) {
                if (keysToIgnore[type].includes(key)) {
                    const objData = item[key];
                    const foundIndex = resultSet.findIndex((x) => x.key === key);
                    if (foundIndex !== -1) {
                        resultSet[foundIndex].data.push(
                            calculateTorqueValue(objData?.value, key)
                        );
                    } else {
                        resultSet.push({
                            key,
                            data: [calculateTorqueValue(objData?.value, key)],
                        });
                    }
                    toPush = true;
                }
            }
        } else {
            for (const key in item) {
                if (keysToIgnore[type].includes(key)) {
                    // Always include Acyclism data; let UI handle filtering based on setting
                    const objData = item[key];
                    const trendKey = trendTitle[type][key] || toTitleCase(key);                    
                    const useRawValue = ["PropellerEfficiency", "Drop in Propeller Efficiency", "Level(RMS)", "Level RMS(mm/s)", "Propeller Fouling", "Twist Angle", "Level"].includes(trendKey);
                    const usePercentageValue = ["Propeller Unbalance"].includes(trendKey);
                    const foundIndex = resultSet.findIndex((x) => x.key === key);
                    
                    let valueToUse = 0;
                    if (usePercentageValue) {
                        valueToUse = objData?.valueInPercent ?? 0;
                    } else if (useRawValue) {
                        valueToUse = objData?.value ? parseFloat(objData?.value) : 0;
                    } else if (type === "Turbocharger") {
                        valueToUse = objData?.valueInPercent ?? 0;
                    } else {
                        valueToUse = objData?.valueInHealth ?? 0;
                    }

                    if (foundIndex !== -1) {
                        resultSet[foundIndex].data.push(valueToUse);
                    } else {
                        resultSet.push({
                            key,
                            data: [valueToUse],
                        });
                    }
                    toPush = true;
                }
            }
        }

        if (toPush) {
            labels.push(getdate(item?.DateAndTime));
            rpmData.push(parseInt(item?.ChannelSpeed || 0));
        }
    }

    const dataSet = [];
    let maxRpm = 0;
    if (rpmData && rpmData.length > 0) {
        const rpmDataArr = buildDataSet(
            type === "Engine" ? "RPM" : "Speed",
            "black",
            rpmData,
            "y1"
        );
        dataSet.push(rpmDataArr);
        maxRpm = rpmDataArr.maxValue;
    }

    for (let item of resultSet) {
        dataSet.push(
            buildDataSet(
                trendTitle[type][item.key]
                    ? trendTitle[type][item.key]
                    : toTitleCase(item.key),
                getRandomColor(),
                item.data
            )
        );
    }
    return { dataSet, labels, maxRpm };
}

/* The above code is defining a JavaScript function called `buildDataSet`. This function takes four
parameters: `title`, `color`, `dataPoints`, and `axisId`. */
function buildDataSet(title, color, datapoints, axisId) {
    /* The above code is defining a function that returns an object with various properties. */
    console.log("buildDataSet title",title,datapoints);

    const numericDatapoints = (datapoints || [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

    const hasValidPoints = numericDatapoints.length > 0;
    
    const isAverage = [
        "Increase in Fuel Consumption",
        "Torque",
        "Power",
        "Zero Torsion",
        "Drop in Propeller Efficiency",
        "Speed",
        "Propeller Fouling",
        "Torsion",
        "Twist Angle"
    ].includes(title);

    const isThreeDecimal = ["Increase in Fuel Consumption"].includes(title);

    const isFourDecimal = [
        "Level",
        "Level(RMS)",
        "Level RMS(mm/s)",
        "Twist Angle",
    ].includes(title);

    const minVal = hasValidPoints
        ? (isFourDecimal
            ? numericDatapoints.reduce((min, value) => Math.min(min, value), numericDatapoints[0]).toFixed(4)
            : isThreeDecimal
            ? numericDatapoints.reduce((min, value) => Math.min(min, value), numericDatapoints[0]).toFixed(3)
            : isAverage
            ? numericDatapoints.reduce((min, value) => Math.min(min, value), numericDatapoints[0]).toFixed(2)
            : Math.round(numericDatapoints.reduce((min, value) => Math.min(min, value), numericDatapoints[0])))
        : (isFourDecimal ? "0.0000" : (isThreeDecimal ? "0.000" : (isAverage ? "0.00" : 0)));

    const maxValue = hasValidPoints
        ? (isFourDecimal
            ? numericDatapoints.reduce((max, value) => Math.max(max, value), numericDatapoints[0]).toFixed(4)
            : isThreeDecimal
            ? numericDatapoints.reduce((max, value) => Math.max(max, value), numericDatapoints[0]).toFixed(3)
            : isAverage
            ? numericDatapoints.reduce((max, value) => Math.max(max, value), numericDatapoints[0]).toFixed(2)
            : Math.round(numericDatapoints.reduce((max, value) => Math.max(max, value), numericDatapoints[0])))
        : (isFourDecimal ? "0.0000" : (isThreeDecimal ? "0.000" : (isAverage ? "0.00" : 0)));

    const avgValue = hasValidPoints
        ? (isFourDecimal
            ? (numericDatapoints.reduce((sum, value) => sum + value, 0) / numericDatapoints.length).toFixed(4)
            : isThreeDecimal
            ? (numericDatapoints.reduce((sum, value) => sum + value, 0) / numericDatapoints.length).toFixed(3)
            : isAverage
            ? (numericDatapoints.reduce((sum, value) => sum + value, 0) / numericDatapoints.length).toFixed(2)
            : roundToNearest10(numericDatapoints.reduce((sum, value) => sum + value, 0) / numericDatapoints.length))
        : (isFourDecimal ? "0.0000" : (isThreeDecimal ? "0.000" : (isAverage ? "0.00" : 0)));

    return {
        title: title,
        data: datapoints,
        label: title,
        borderWidth: 1.5,
        pointRadius: 0,
        tension: 0,
        borderColor: color,
        pointBackgroundColor: color,
        backgroundColor: color,
        fill: false,
        yAxisID: axisId ?? "y",
        hidden: false,
        minVal,
        maxValue,
        avgValue,
    };
}

export function convertDate(dateVal, isEnd = false) {
    const date = new Date(dateVal);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const time = isEnd ? '23:59:59' : '00:00:00';
    return `${yyyy}-${mm}-${dd} ${time}`;
}

export function convertDateOnly(dateVal) {
    let dateExtract = format(dateVal, "yyyy-MM-dd");
    return dateExtract;
}

export function convertUTCDateToLocalTime(dateVal) {
    let dateExtract = format(dateVal, "dd-MMM-yyyy HH:mm:ss");
    return dateExtract;
}

export function formatDateSafe(value) {
    if (!value) return "";

    const str = String(value).trim();

    // Case 1: 10-digit UNIX timestamp (seconds)
    if (/^\d{10}$/.test(str)) {
        const date = new Date(Number(str) * 1000);
        return format(date, "dd-MMM-yyyy HH:mm:ss");
    }

    // Case 2: formatted date string
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
        return format(date, "dd-MMM-yyyy HH:mm:ss");
    }

    return value; // fallback
}

function percentage(partialValue, totalValue) {
    return (100 * partialValue) / totalValue;
}

function buildTurbineChart(data, key, key2, title, isGradientOpposite) {
    let labels = [];
    let datapoints = [];
    let datapoints2 = [];
    let zAxisDataPoints = [];
    let count = 0;
    if (data) {
        for (let item of data) {
            const moduleData = JSON.parse(item["jsondata"]);
            labels.push(getdate(moduleData?.DateAndTime));

            /**Rohit */
            zAxisDataPoints.push(
                (() => {
                    for (const key in moduleData) {
                        if (key.startsWith("Aux")) {
                            const val = parseInt(moduleData[key]?.PowerPercent?.Value);
                            if (!isNaN(val) && val > 0) {
                                return val;
                            }
                        }
                    }
                    return parseInt(moduleData.ChannelSpeed || 0);
                })()
            );
            const valueObject = moduleData[key];
            datapoints.push(round(valueObject?.value || 0));
            if (key2) {
                const valueObject2 = moduleData[key2];
                datapoints2.push(round(valueObject2?.valueInHealth || 0));
            }
            count++;
        }
    }
    let sum = 0;
    for (let i of datapoints) {
        sum += parseInt(i, 10); //don't forget to add the base
    }

    let avg = sum / datapoints.length;

    return {
        trendsName: title,
        speedName: "Speed",
        min: round(Math.min(...datapoints)),
        max: round(Math.max(...datapoints)),
        yMax: 110,
        avg: round(avg),
        datapoints: datapoints,
        dataPointsY1: zAxisDataPoints,
        dataPointsY2: datapoints2,
        labels: labels,
        chartType: "LineGradient",
        yLabel: "Health Percent",
        xLabel: "Time",
        isGradientOpposite: isGradientOpposite ?? false,
    };
}

function buildPropeller1Chart(data, key, key2, title, isGradientOpposite) {
    let labels = [];
    let datapoints = [];
    let datapoints2 = [];
    let zAxisDataPoints = [];
    let count = 0;
    if (data) {
        for (let item of data) {
            const moduleData = JSON.parse(item["jsondata"]);
            labels.push(getdate(moduleData?.DateAndTime));

            /**Rohit */
            zAxisDataPoints.push(
                (() => {
                    for (const key in moduleData) {
                        if (key.startsWith("Aux")) {
                            const val = parseInt(moduleData[key]?.PowerPercent?.Value);
                            if (!isNaN(val) && val > 0) {
                                return val;
                            }
                        }
                    }
                    return parseInt(moduleData.ChannelSpeed || 0);
                })()
            );
            const valueObject = moduleData[key];
            datapoints.push(Number(valueObject?.value || 0));
            if (key2) {
                const valueObject2 = moduleData[key2];
                datapoints2.push(round(valueObject2?.valueInHealth || 0));
            }
            count++;
        }
    }
    let sum = 0;
    for (let i of datapoints) {
        sum += Number(i);
    }

    let avg = sum / datapoints.length;

    return {
        trendsName: title,
        speedName: "Speed",
        min: round(Math.min(...datapoints)),
        max: round(Math.max(...datapoints)),
        yMax: 1,
        avg: round(avg),
        datapoints: datapoints,
        dataPointsY1: zAxisDataPoints,
        dataPointsY2: datapoints2,
        labels: labels,
        chartType: "LineGradient",
        yLabel: "Propeller Fouling",
        xLabel: "Time",
        isGradientOpposite: isGradientOpposite ?? false,
    };
}

function buildPropellerChart(data, key, key2, title, isGradientOpposite) {
    let labels = [];
    let datapoints = [];
    let datapoints2 = [];
    let zAxisDataPoints = [];
    let count = 0;
    if (data) {
        for (let item of data) {
            const moduleData = JSON.parse(item["jsondata"]);
            labels.push(getdate(moduleData?.DateAndTime));

            /**Rohit */
            zAxisDataPoints.push(
                (() => {
                    for (const key in moduleData) {
                        if (key.startsWith("Aux")) {
                            const val = parseInt(moduleData[key]?.PowerPercent?.Value);
                            if (!isNaN(val) && val > 0) {
                                return val;
                            }
                        }
                    }
                    return parseInt(moduleData.ChannelSpeed || 0);
                })()
            );
            const valueObject = moduleData[key];
            datapoints.push(round(valueObject?.valueInPercent || 0));
            if (key2) {
                const valueObject2 = moduleData[key2];
                datapoints2.push(round(valueObject2?.valueInHealth || 0));
            }
            count++;
        }
    }
    let sum = 0;
    for (let i of datapoints) {
        sum += parseInt(i, 10); //don't forget to add the base
    }

    let avg = sum / datapoints.length;

    return {
        trendsName: title,
        speedName: "Speed",
        min: round(Math.min(...datapoints)),
        max: round(Math.max(...datapoints)),
        yMax: 100,
        avg: round(avg),
        datapoints: datapoints,
        dataPointsY1: zAxisDataPoints,
        dataPointsY2: datapoints2,
        labels: labels,
        chartType: "LineGradient",
        yLabel: "Propeller Unbalance",
        xLabel: "Time",
        isGradientOpposite: isGradientOpposite ?? false,
    };
}

function buildMotorChart(data, key, key2, title, isGradientOpposite) {
    let labels = [];
    let datapoints = [];
    let datapoints2 = [];
    let zAxisDataPoints = [];
    let count = 0;
    if (data) {
        for (let item of data) {
            const moduleData = JSON.parse(item["jsondata"]);
            labels.push(getdate(moduleData?.DateAndTime));

            /**Rohit */
            zAxisDataPoints.push(
                (() => {
                    for (const key in moduleData) {
                        if (key.startsWith("Aux")) {
                            const val = parseInt(moduleData[key]?.PowerPercent?.Value);
                            if (!isNaN(val) && val > 0) {
                                return val;
                            }
                        }
                    }
                    return parseInt(moduleData.ChannelSpeed || 0);
                })()
            );
            const valueObject = moduleData[key];
            datapoints.push(round(valueObject?.valueInHealth || 0));
            if (key2) {
                const valueObject2 = moduleData[key2];
                datapoints2.push(round(valueObject2?.valueInHealth || 0));
            }
            count++;
        }
    }
    let sum = 0;
    for (let i of datapoints) {
        sum += parseInt(i, 10); //don't forget to add the base
    }

    let avg = sum / datapoints.length;

    return {
        trendsName: title,
        speedName: "Speed",
        min: round(Math.min(...datapoints)),
        max: round(Math.max(...datapoints)),
        yMax: 110,
        avg: round(avg),
        datapoints: datapoints,
        dataPointsY1: zAxisDataPoints,
        dataPointsY2: datapoints2,
        labels: labels,
        chartType: "LineGradient",
        yLabel: "Health Percent",
        xLabel: "Time",
        isGradientOpposite: isGradientOpposite ?? false,
    };
}
function buildBearing1Chart(data, key, key2, title, isGradientOpposite) {
    let labels = [];
    let datapoints = [];
    let datapoints2 = [];
    let zAxisDataPoints = [];
    let count = 0;
    if (data) {
        for (let item of data) {
            const moduleData = JSON.parse(item["jsondata"]);
            labels.push(getdate(moduleData?.DateAndTime));

            /**Rohit */
            zAxisDataPoints.push(
                (() => {
                    for (const key in moduleData) {
                        if (key.startsWith("Aux")) {
                            const val = parseInt(moduleData[key]?.PowerPercent?.Value);
                            if (!isNaN(val) && val > 0) {
                                return val;
                            }
                        }
                    }
                    return parseInt(moduleData.ChannelSpeed || 0);
                })()
            );
            const valueObject = moduleData[key];
            datapoints.push(round(valueObject?.value || 0));
            if (key2) {
                const valueObject2 = moduleData[key2];
                datapoints2.push(round(valueObject2?.valueInHealth || 0));
            }
            count++;
        }
    }
    let sum = 0;
    for (let i of datapoints) {
        sum += parseInt(i, 10); //don't forget to add the base
    }

    let avg = sum / datapoints.length;
    return {
        trendsName: title,
        speedName: "Speed",
        min: round(Math.min(...datapoints)),
        max: round(Math.max(...datapoints)),
        yMax: 10,
        avg: round(avg),
        datapoints: datapoints,
        dataPointsY1: zAxisDataPoints,
        dataPointsY2: datapoints2,
        labels: labels,
        chartType: "LineGradient",
        yLabel: "Level (mm/sec)",
        xLabel: "Time",
        isGradientOpposite: isGradientOpposite ?? false,
    };
}

function buildBearingChart(data, key, key2, title, isGradientOpposite, valueKey = "valueInHealth", valueKey2 = "valueInHealth", key3 = null, valueKey3 = "valueInHealth", includeSpeed = true) {
    let labels = [];
    let datapoints = [];
    let datapoints2 = [];
    let datapoints3 = [];
    let zAxisDataPoints = [];
    let count = 0;
    if (data) {
        for (let item of data) {
            const moduleData = JSON.parse(item["jsondata"]);
            labels.push(getdate(moduleData?.DateAndTime));

            if (includeSpeed) {
                zAxisDataPoints.push(
                    (() => {
                        for (const k in moduleData) {
                            if (k.startsWith("Aux")) {
                                const val = parseInt(moduleData[k]?.PowerPercent?.Value);
                                if (!isNaN(val) && val > 0) {
                                    return val;
                                }
                            }
                        }
                        return parseInt(moduleData.ChannelSpeed || 0);
                    })()
                );
            }

            const valueObject = moduleData[key];
            datapoints.push(round(valueObject?.[valueKey] || 0));
            if (key2) {
                const valueObject2 = moduleData[key2];
                datapoints2.push(round(valueObject2?.[valueKey2] || 0));
            }
            if (key3) {
                const valueObject3 = moduleData[key3];
                datapoints3.push(round(valueObject3?.[valueKey3] || 0));
            }
            count++;
        }
    }
    let sum = 0;
    for (let i of datapoints) {
        sum += parseInt(i, 10); //don't forget to add the base
    }

    let avg = sum / datapoints.length;
    return {
        trendsName: title,
        speedName: "Speed",
        min: round(Math.min(...datapoints)),
        max: round(Math.max(...datapoints)),
        yMax: 110,
        avg: round(avg),
        datapoints: datapoints,
        dataPointsY1: includeSpeed ? zAxisDataPoints : [],
        dataPointsY2: datapoints2,
        dataPointsY3: datapoints3,
        labels: labels,
        chartType: "LineGradient",
        yLabel: "Health Percent",
        xLabel: "Time",
        isGradientOpposite: isGradientOpposite ?? false,
    };
}

export function buildTurbochargerChart(data, key, key2, title, isGradientOpposite, valueKey = "valueInHealth", valueKey2 = "valueInHealth", key3 = null, valueKey3 = "valueInHealth") {
    return buildBearingChart(data, key, key2, title, isGradientOpposite, valueKey, valueKey2, key3, valueKey3, false);
}

const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "June",
    "July",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

function getdate(firstKey) {
    const date = new Date(firstKey);
    return (
        month[date.getMonth()] +
        " " +
        date.getDate() +
        ", " +
        date.getHours() +
        ":" +
        date.getMinutes()
    );
}

export function buildAuxData(data) {
    data = JSON.parse(data);

    const firstKey = Object.keys(data)[0];
    const itemData = data[firstKey];

    let id = 0;
    const returnData = [];

    // 🔥 Loop through all Aux channels (Aux1, Aux2, Aux3...)
    for (const itemKey in itemData) {
        if (!itemKey.startsWith("Aux")) continue;

        const auxData = itemData[itemKey];
        if (!auxData) continue;

        for (const key of Object.keys(auxData)) {
            if (
                key !== "ID" &&
                key !== "DateAndTime" &&
                key !== "DeviceType" &&
                key !== "DeviceName"
            ) {
                const val = auxData[key];
                if (val?.Value && val.Value !== "NA") {
                    returnData.push({
                        id: id++,
                        auxKey: itemKey,
                        parameterKey: key,
                        indicatorMax: val?.Max ?? 0,
                        indicatorMin: val?.Min ?? 0,
                        // Example: Aux1 Power Percent (%)
                        indicatorName: `${itemKey} ${val?.Desc} (${val?.Unit})`,
                        indicatorValue: Math.round(val?.Value),
                        indicatorUnit: val?.Unit ?? "",
                        indicatorDesc: val?.Desc ?? key,
                        isGradientColor: false,
                        isPercentage: val?.Unit === "%",
                    });
                }
            }
        }
    }

    return returnData;
}


export function useQuery() {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
}

const max_value = {
    PowerPercent: { max: 250.99, min: -251 },
    Power: { max: 2211081215, min: -2000000000 },
    FuelLevel: { max: 250.99, min: -251 },
    EngineOilTemp: { max: 1735, min: -273 },
    EngineOilPressure: { max: 8031.87, min: 0 },
    EngineCoolantTemp: { max: 1735, min: -273 },
    BatteryVoltage: { max: 3212.75, min: 0 },
    EngineRPM: { max: 8031.87, min: 0 },
    OperatingHours: { max: 210554060.75, min: 0 },
    FuelPressure: { max: 0.125, min: 0 },
    CrankcasePressure: { max: 251.99, min: -250 },
    BoostPressure: { max: 8031.87, min: 0 },
};

function roundToNearest10(number) {
    return Math.round(number / 10) * 10;
}

function toTitleCase(text) {
    // Only split camelCase boundaries, not every uppercase letter.
    // This keeps labels like Aux_DG1_GEN_KW_LOAD_PERCENT readable and unchanged.
    const result = text.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    return (result.charAt(0).toUpperCase() + result.slice(1)).trim();
}

function getRandomColor() {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
}

const keysToIgnore = {
    Engine: [
        "PowerLoss",
        "Unbalance",
        "EngineEfficiency",
        // "CamPump",
        "Damper",
        "Acyclism",
        "MechanicalHealth",
        "Compression",
        "Bearing",
        "BearingBis",
        "InjectionCondition",
        "Misfiring",
        "Pressure"
    ],
    Turbine: [
        "RegularityDeviation",
        "BearingStatus",
        "BladeStatus",
        "combustionCondition",
        "BearingStatusGas",
        "CombustionKit",
        "TurbineCoupling",
    ],
    Propeller: [
        // "PropellerEfficiency",
        "PropellerUnbalance",
        "PropellerFouling",
        // "PropellerBearing"
    ],
    Motor: ["MStressStability", "MElectromag", "MBearing"],
    Bearing: [
        "BearingGlobal",
        "GlobalMixed",
        "GlobalKurto",
        "GlobalLevel",
        "2KMixed",
        "4KMixed",
        "8KMixed",
    ],
    Turbocharger: [
        "GlobalMixed",
        "GlobalKurto",
        "GlobalLevel",
        "4KMixed",
    ],
    Torque: ["StaticTorsion", "Static_zeroTorsion", "StaticTorque", "StaticPower"],
};

const trendTitle = {
    Engine: {
        PowerLoss: "Increase in Fuel Consumption",
        Unbalance: "Performance of Mounts & Supports",
        EngineEfficiency: "Combustion Condition",
        // CamPump: "Governor, Crank driven Accessories Health",
        Damper: "Performance of Vibration Damper",
        Acyclism: "Twist Angle",
        MechanicalHealth: "Engine Health",
        Compression: "Compression Condition",
        Injection: "Injection Condition",
        Bearing: "Bearing Condition",
        BearingBis: "Condition of cyl moving parts",
        InjectionCondition: "Fuel Injection Performance",
        Misfirng: "Misfiring",
        Pressure: "Pressure"
    },
    Turbine: {
        RegularityDeviation: "Regularity/Deviation",
        MBearing: "Bearing Status",
        BladeStatus: "Shaft/Blades Health",
        combustionCondition: "Combustion Kit",
        BladeStatusGas: "Shaft /Blades Health",
        CombustionKit: "Combustion Kit",
        TurbineCoupling: "Coupling/Alignment",
    },
    Propeller: {
        // PropellerEfficiency: "Drop in Propeller Efficiency",
        PropellerUnbalance: "Propeller Unbalance",
        // PropellerBearing: "Stern Tube Bearing",
        PropellerFouling: "Propeller Fouling",
    },
    Motor: {
        MStressStability: "Stability",
        MElectromag: "Electromagnetic Stress",
        MBearing: "Bearing",
    },
    Bearing: {
        BearingGlobal: "Mechanical Health",
        GlobalMixed: "Global(Umbalance/Alignment/Loosness)",
        GlobalKurto: "Shock Index",
        GlobalLevel: "Level(RMS)",
        "2KMixed": "Shaft/Clearance",
        "4KMixed": "Bearings",
        "8KMixed": "Friction",
    },
    Turbocharger: {
        GlobalMixed: "Unbalance Instability",
        GlobalKurto: "Shock index",
        GlobalLevel: "Level RMS(mm/s)",
        "4KMixed": "Bearing Damage",
    },
    Torque: {
        StaticTorsion: "Torsion",
        Static_zeroTorsion: "Zero Torsion",
        StaticTorque: "Torque(kNm)",
        StaticPower: "Power(kW)",
    },
};
const trendTooltip = {
    "Engine Health":
        "Overall indication of mechanical condition of Engine. Indicates the minor and major mechanical problems in the moving parts of the engine.Calculated from the wear/friction, cylinder pressure and crankshaft bearings indicators.",
    "Combustion Condition":
        "Indication of Engine running efficiently with regard to Fuel Consumption.Calculated from the injection condition and compression indicators.Even when this indicator approaches the warning limits, the engine can still be kept in operation without concern of damage to the engine as long as the mechanical condition indicator is normal.The consequences of allowing the engine to continue running under this condition are only higher operational costs and higher fuel consumption.",
    "Performance of Mounts & Supports":
        "Mounts indicator provides information about the health of Supporting structures, Excessive frame vibrations.Mount Supports have weakened due to ageing/weathering/lost elasticity/spoilt due to liquid leaks",
    //"Governor, Crank driven Accessories Health":
    //    "This indicator measures irregularities of external/auxilliary equipment directly driven by the Governor and crankshaft.Irregularities in control system/fuel regulation system, turbocharger valves,fuel/lube/water pumps, turbocharger, coulping, shaft line or screw.",
    "Performance of Vibration Damper":
        "Damper indicator provides information about the effectiveness of rotational vibration absorption by Damper. When Damper health is low, the vibrations from the engine are transferred onto the gearboxes/propellers or associated driven systems. ",
    "Increase in Fuel Consumption":
        "Overall indication of engine imbalance, expressed as a percentagem, due to non-optimal thermal health(refer to indicators) and the inertia resistance of the rotating and moving parts.",
    "Twist Angle":
        "Measures the cyclic variation in engine speed (acyclism) indicating fuel injection timing irregularities or combustion imbalances across cylinders. High twist angle values suggest uneven firing or injection defects requiring attention.",
    "Compression Condition": "Indicates deviations in compression due to leakage and/or bad timing of the valves. Compression problems due to: incorrect valve timing, leakage due to a damaged valve or valve seat or major damage in liner or piston ring",
    "Injection Condition": "Indicates deviations of fuel injection due to a defective fuel pump. Imbalance in peak firing pressure. Worn liners or timing in the compression stroke of one or more cylinders or Fuel is injected asynchronously.",
    "Bearing Condition": "The dynamic behavior of the bearings and all moving parts( crankshaft, conrods, Tierods, pistons, connecting rods, writs pins, bearings). Measures per cylinder over multiple revolutions.",
    "Condition of cyl moving parts": "The dynamic behavior of the bearings and all moving parts( crankshaft, conrods, Tierods, pistons, connecting rods, writs pins, bearings). Measures per cylinder over multiple revolutions.",
    "Misfiring": "Indicates Misfiring at the Cylinders during Gas Engine Operation",
    //motor
    Stability:
        "This indicator tracks the degradation of components of electromagnetic circuits during operation",
    
    "Bearing Damage":
        "This indicator monitors dynamic stresses on the bearings or play and wear of the guide elements,and bearing degradation during operation",
    "Electromagnetic Stress":
        "This indicator expresses the electromagnetic stresses resulting from the windings and rotor during operation.",
    //torque
    "Torsion(degree)": "Torsion In degrees",
    "Torque(kNm)": "Torque in kNm",
    "Power(kW)": "Power in kW",
    RPM: "Speed",
    Speed: "Speed",
    //turbine
    "Regularity/Deviation":
        "Indicates the Faults in the steam intake system or lubrication control system of the bearings and thrust bearings",
    "Bearing Status":
        'Condition of Bearings due to Insufficient load due to: - coupling malfunction (dynamic misalignment; see "Coupling" indicator). - structure/support bending - excessive dynamic gearmesh force  (see gearbox levels). - Bearing wear - Cylinder defect (bore/bearing shell) - Stator geometry defect - Friction (dynamic seal defects) - Axial stability between the thrust bearings',
    "Shaft/Blade Health":
        "Indicates the overall health of Shaft/Blade and any deterioration due to Loss of clamping, dynamic instabilities, damage to rotating/ Fixed blades",
    Coupling:
        "This indicator expresses the fatigue during operation of the coupling during each cycle.It also describes a variable dynamic misalignment (isokinetic defect) between the turbine and the coupled element of the shaft line",
    "Combustion Kit":
        "This indicator expresses the fatigue during operation of the coupling during each cycle.It also describes a variable dynamic misalignment (isokinetic defect) between the turbine and the coupled element of the shaft line",
    //Bearing
    Bearings:
        "Assembly irregularity Roller bearing case:Incorrect alignment of the inner ring with the outer ring.Race spill, axial sliding irregularities (in the case of a free bearing) between outer ring and housing (stick slip, wear housing?Insufficient axial preload in the case of a preloaded bearing (angular contact).Irregularities of the rolling of the bearing elements on the races (inner/outer race) due to excessive clearance related to an underload in a radial direction.Damage of a bearing element (cage / elements / races).Wear of pulleys / belts.Defective bearing between the bearing outer ring and the bore.Wear of the housingPlain bearing case:Instability due to axial excessive clearance in the case of a thrust bearing, radial in the case of the guide bearingPadsbearing case:Pivot wearDifficulties of pivoting pads (jamming)",
    Friction:
        "Assembly irregularityRoller bearing case:Incorrect alignment of the inner ring with the outer ring.Race spill, axial sliding irregularities (in the case of a free bearing) between outer ring and housing (stick slip, wear housing?Insufficient axial preload in the case of a preloaded bearing (angular contact).Irregularities of the rolling of the bearing elements on the races (inner/outer race) due to excessive clearance related to an underload in a radial direction.Damage of a bearing element (cage / elements / races).Wear of pulleys / belts.Defective bearing between thebearing outer ring and the bore.Wear of the housingPlain bearing case:Instability due to axial excessive clearance in the case of a thrust bearing, radial in the case of the guide bearing Pads bearing case:Pivot wearDifficulties of pivoting pads (jamming)",
    "Mechanical Health":
        "Overall indication of mechanical condition of Bearings. Also Identifies if the continuation of the operation presents an aggravation of the damages evolving towards the loss of function. Then, An intervention is to be expected",
    "Global(Umbalance/Alignment/Loosness)":
        "Roller bearing case-  Without high choc indexUmbalance,misalignment Cantilever rotor umbalance Mode (bending,tilting) excitation-  With high choc index value Loosening and / or clearance at the support structure (assembly at the supports)Coupling: clearance, clamping wear (toothed coupling), axial distance not respected (coupling with flexible blades), cracking (flector) Degradation of bearingsPlain bearings case:Without high choc index Umbalance,misalignment Cantilever rotor umbalance-  With high choc index valueCoupling: clearance,clamping wear (toothed coupling), axial distance not respected (coupling with flexible blades), cracking (flector)Loosening of caps and supports",
    "Shock Index":
        "This indicator measures the presence of irregular pulses (shocks),translating a malfunction in the mechanical behavior of the component",
    "Shock index":
        "This indicator measures the presence of irregular pulses (shocks), translating a malfunction in the mechanical behavior of the component.",
    "Level(RMS)":
        "This indicator gives an overall level (2Hz-1KHz) in mm /s and compared to the ISO standard 10816.",
    "Level RMS(mm/s)":
        "This indicator gives the overall vibration level in mm/sec for Turbocharger monitoring.",
    "Unbalance Instability":
        "This indicator highlights unbalance and instability conditions linked to rotor/support alignment and structural looseness.",
    "Shaft/Clearance":
        "Excessive shaft displacement,Bad guiding effect:Roller bearing case :Excessive clearance in all type of bearings Excessive excentricity (static/dynamic) in the case of electric motors : electromagnetic circuit defectExcessive tipping displacement in case of vertical installations Excessive stresses causing the shaft to move with high displacement Excessive teeth mesh stresses in the case of a gearbox Plain bearing case :Clearance between bearing pad/bearing cap Excessive excentricity (static/dynamic) in the case of electric motors : electromagnetic circuit defect Excessive tipping displacement in case of vertical installations Excessive teeth mesh stresses in the case of a gearbox Excessive lubrification",
    "Drop in Efficiency":
        "Indicates the loss in propeller efficiency due to hydrodynamic issues. A high drop means the propeller is not converting engine power effectively, possibly due to fouling, pitch error, or hull resistance.",
    "Propeller Unbalance":
        "Measures the level of mass imbalance in the rotating propeller. A low health score here means the propeller is significantly out of balance, which can cause excessive vibration, stress on the shaft, and damage to bearings.",
    "Propeller Fouling":
        "Indicates the extent of fouling on the propeller blades. Fouling can lead to increased drag, reduced thrust, and higher fuel consumption. A poor health score suggests significant fouling that needs attention.",
    "Stern Tube Bearing Health":
        "Indicates the condition of bearings supporting the propeller shaft. Poor health suggests wear, lubrication loss, or misalignment."
};

export function buildPngReportData(data, modelType, formData) {
    const maxRPMThrshhold = parseInt(formData.rated_rpm);
    const maxPower = parseInt(formData.power);
    let globalIndicator = [];
    let cylinder_specific_indicators = [];
    let pressureReadings = [];

    if (modelType === "Engine") {
        if (data?.MechanicalHealth && data?.MechanicalHealth.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Engine Health",
                    data?.MechanicalHealth,
                    "valueInHealth"
                )
            );
        }
        if (data?.EngineEfficiency && data?.EngineEfficiency.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Combustion Condition",
                    data?.EngineEfficiency,
                    "valueInHealth"
                )
            );
        }
        if (data?.ChannelSpeed) {
            globalIndicator.push(
                buildRpmData("RPM", data?.ChannelSpeed, maxRPMThrshhold)
            );
        }
        /**if (data?.ChannelSpeed) {
            globalIndicator.push(
              buildRpmData("ChannelSpeed", data?.ChannelSpeed, /* MaxThresholdForChannelSpeed/)
            );
          }*/
        if (data?.Unbalance && data?.Unbalance.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Performance of Mounts & Supports",
                    data?.Unbalance,
                    "valueInHealth"
                )
            );
        }
        // if (data?.CamPump && data?.CamPump.valueInHealth) {
        //     globalIndicator.push(
        //         buildIndicatorData(
        //             "Governor, Crank driven Accessories Health",
        //             data?.CamPump,
        //             "valueInHealth"
        //         )
        //     );
        // }
        if (data?.Damper && data?.Damper.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Performance of Vibration Damper",
                    data?.Damper,
                    "valueInHealth"
                )
            );
        }
        if (
            isTwistAngleEnabled() &&
            data?.Acyclism &&
            data?.Acyclism.value !== undefined &&
            data?.Acyclism.value !== null
        ) {
            globalIndicator.push(
                buildIndicatorData(
                    "Twist Angle",
                    data?.Acyclism,
                    "value"
                )
            );
        }
        if (data?.PowerLoss && data?.PowerLoss.value) {
            globalIndicator.push(
                buildIndicatorData(
                    "Increase in Fuel Consumption",
                    data?.PowerLoss,
                    "value",
                    true
                )
            );
        }

        const moduleData = data["Engine"];
            const firingOrderLabel = resolveFiringOrderLabels(
                formData?.firing_order,
                moduleData["FiringOrder"]
            );
            const firingOrderSplit = Math.ceil(firingOrderLabel.length / 2);
            const first = firingOrderLabel.slice(0, firingOrderSplit);
            const second = firingOrderLabel.slice(first.length);
        if (data?.Compression) {
            const compression = buildCompressionData(
                first,
                second,
                data["Compression"],
                "Compression Condition",
                firingOrderLabel
            );
            if (compression) {
                cylinder_specific_indicators.push(compression);
            }
        }
        if (data?.InjectionCondition) {
            const injection_Condition = buildCompressionData(
                first,
                second,
                data["InjectionCondition"],
                "Injection Condition",
                firingOrderLabel
            );
            if (injection_Condition) {
                cylinder_specific_indicators.push(injection_Condition);
            }
        }
        if (data?.Bearing) {
            const bearing_Condition = buildCompressionData(
                first,
                second,
                data["Bearing"],
                "Bearing Condition",
                firingOrderLabel
            );
            if (bearing_Condition) {
                cylinder_specific_indicators.push(bearing_Condition);
            }
        }
        if (data?.BearingBis) {
            const condition_of_cyl_moving_parts = buildCompressionData(
                first,
                second,
                data["BearingBis"],
                "Condition of cyl moving parts",
                firingOrderLabel
            );
            if (condition_of_cyl_moving_parts) {
                cylinder_specific_indicators.push(condition_of_cyl_moving_parts);
            }
        }
        /* if (
       data?.Pressure &&
       data?.Pressure.cylinderValues &&
       data?.FiringOrder &&
       Array.isArray(data.FiringOrder) && // Check if FiringOrder is an array
       data.FiringOrder.length > 0 // Check if FiringOrder contains valid data
     ) {
       const firingOrder = data.FiringOrder;
       const cylinderValues = data.Pressure.cylinderValues;
 
       for (let i = 0; i < firingOrder.length; i++) {
         const cylinderIndex = firingOrder[i] - 1; // Adjust for 0-based index
         const cylinderValue = cylinderValues[cylinderIndex];
 
         pressureReadings.push({
           order: firingOrder[i],
           value: cylinderValue.toString(),
         });
       }
     }
 
     console.log("Pressure Readings:", pressureReadings);  */
        // if (
        //     data?.Pressure &&
        //     data?.Pressure.cylinderValues
        // ) {
        //     //const firingOrder = moduleData["FiringOrder"];
        //     const cylinderValues = data.Pressure.cylinderValues;

        //     for (let i = 0; i < firingOrder.length; i++) {
        //         let cylinderIndex = firingOrder[i] - 1; // Adjust for 0-based index
        //         if (isNaN(parseInt(firingOrder[i]))) {
        //             // firingOrder can be A1, A2, B2, etc...
        //             // assuming equal number of cylinders per letter
        //             const cylindersPerLetter = Math.floor(firingOrder.length / 2);
        //             const letter = firingOrder[i][0];

        //             if (letter === 'A' || letter === 'L') {
        //                 cylinderIndex = parseInt(firingOrder[i].slice(1)) - 1;
        //             }                    
        //             else if (letter === 'B' || letter === 'R') {
        //                 cylinderIndex = parseInt(firingOrder[i].slice(1)) - 1 + cylindersPerLetter;
        //             }
        //         }

        //         const cylinderValue = cylinderValues[cylinderIndex];

        //         pressureReadings.push({
        //             order: firingOrder[i],
        //             value: cylinderValue.toString(),
        //         });
        //     }
        // }
        if (data?.Pressure && data?.Pressure.cylinderValues && firingOrderLabel.length > 0) {
            const cylinderValues = data.Pressure.cylinderValues;
            for (let i = 0; i < Math.min(firingOrderLabel.length, cylinderValues.length); i++) {
                const cylinderValue = cylinderValues[i];

                pressureReadings.push({
                    order: firingOrderLabel[i],
                    value: cylinderValue?.toString() ?? "N/A",
                });
            }
        }


        if (
            formData["fuel"] === "Hydrogen" ||
            formData["fuel"] === "Ammonia" ||
            formData["fuel"] === "Natural Gas"
        ) {
            const miss_firing = buildCompressionData(
                first,
                second,
                data["Misfiring"],
                "Misfiring",
                firingOrderLabel
            );
            if (miss_firing) {
                cylinder_specific_indicators.push(miss_firing);
            }
        }
        return {
            globalIndicator,
            cylinder_specific_indicators,
            pressureReadings
        };
    }

    else if (modelType === "Torque") {
        if (data?.Status <= 0) {
            return {
                globalIndicator,
                cylinder_specific_indicators,
            };
        }
        if (data?.StaticTorsion && data?.StaticTorsion.value) {
            globalIndicator.push(
                buildTorqueIndicatorData(
                    "Torsion(degree)",
                    data.StaticTorsion?.value || 0,
                    6
                )
            );
        }
        if (data?.StaticTorque && data?.StaticTorque.value) {
            globalIndicator.push(
                buildTorqueIndicatorData(
                    "Torque(kNm)",
                    (data.StaticTorque?.value || 0) * 0.001,
                    1000
                )
            );
        }
        if (data?.StaticPower && data?.StaticPower.value) {
            globalIndicator.push(
                buildTorqueIndicatorData(
                    "Power(kW)",
                    (data.StaticPower?.value || 0) * 0.001,
                    maxPower * 0.001
                )
            );
        }
        if (data?.ChannelSpeed) {
            globalIndicator.push(
                buildTorqueIndicatorData(
                    "Speed",
                    data.ChannelSpeed || 0,
                    maxRPMThrshhold
                )
            );
        }
    } else if (modelType === "Turbine") {
        if (data?.ChannelSpeed) {
            globalIndicator.push(
                buildRpmData("Speed", data?.ChannelSpeed, maxRPMThrshhold)
            );
        }
        if (data?.RegularityDeviation && data?.RegularityDeviation.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Regularity/Deviation",
                    data?.RegularityDeviation,
                    "valueInHealth"
                )
            );
        }
        if (data?.BearingStatus && data?.BearingStatus.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Bearing Status",
                    data?.BearingStatus,
                    "valueInHealth"
                )
            );
        }
        if (data?.BearingStatusGas && data?.BearingStatusGas.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Bearing Status ",
                    data?.BearingStatusGas,
                    "valueInHealth"
                )
            );
        }
        if (data?.BladeStatus && data?.BladeStatus.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData("Shaft Health", data?.BladeStatus, "valueInHealth")
            );
        }

        if (formData.type === "Steam") {
            if (data?.TurbineCoupling && data?.TurbineCoupling.valueInHealth) {
                globalIndicator.push(
                    buildIndicatorData("Coupling", data?.TurbineCoupling, "valueInHealth")
                );
            }
        } else {
            if (data?.CombustionKit && data?.CombustionKit.valueInHealth) {
                globalIndicator.push(
                    buildIndicatorData(
                        "Combustion Kit",
                        data?.CombustionKit,
                        "valueInHealth"
                    )
                );
            }
        }
    } else if (modelType === "Propeller") {
        // if (data?.PropellerEfficiency && data?.PropellerEfficiency.value) {
        //     globalIndicator.push(
        //         buildIndicatorData("Drop in Efficiency", data?.PropellerEfficiency, "value")
        //     );
        // }
        if (data?.PropellerUnbalance && data?.PropellerUnbalance.valueInPercent) {
            globalIndicator.push(
                buildIndicatorData("Propeller Unbalance", data?.PropellerUnbalance, "valueInPercent")
            );
        }
        if ((data?.PropellerFouling || data?.PropellerFooling) && (data?.PropellerFouling || data?.PropellerFooling).value) {
            const foulingData = data?.PropellerFouling || data?.PropellerFooling;
            globalIndicator.push(
                buildIndicatorData("Propeller Fouling", foulingData, "value", true, "Propeller")
            );
        }
        // if (data?.PropellerBearing && data?.PropellerBearing.valueInHealth) {
        //     globalIndicator.push(
        //         buildIndicatorData("Stern Tube Bearing", data?.PropellerBearing, "valueInHealth")
        //     );
        // }
        if (data?.ChannelSpeed) {
            globalIndicator.push(
                buildRpmData("Speed", data?.ChannelSpeed, maxRPMThrshhold)
            );
        }
    } else if (modelType === "Motor") {
        if (data?.MStressStability && data?.MStressStability.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData("Stability", data?.MStressStability, "valueInHealth")
            );
        }
        if (data?.MBearing && data?.MBearing.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData("Bearing", data?.MBearing, "valueInHealth")
            );
        }
        if (data?.ChannelSpeed) {
            globalIndicator.push(
                buildRpmData("Speed", data?.ChannelSpeed, maxRPMThrshhold)
            );
        }
        if (data?.MElectromag && data?.MElectromag.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Electromagnetic Stress",
                    data?.MElectromag,
                    "valueInHealth"
                )
            );
        }
    } else if (modelType === "Bearing") {
        if (data?.BearingGlobal && data?.BearingGlobal.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Mechanical Health",
                    data?.BearingGlobal,
                    "valueInHealth"
                )
            );
        }
        if (data?.GlobalLevel && data?.GlobalLevel.valueInHealth) {
            globalIndicator.push(
                buildTorqueIndicatorData(
                    "Level RMS(mm/s)",
                    data.GlobalLevel?.value || 0,
                    10
                )
            );
        }
        if (data?.GlobalKurto && data?.GlobalKurto.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData("Shock Index", data?.GlobalKurto, "valueInHealth")
            );
        }
        if (data["4KMixed"] && data["4KMixed"].valueInHealth) {
            globalIndicator.push(
                buildIndicatorData("Bearings", data["4KMixed"], "valueInHealth")
            );
        }
        if (data?.ChannelSpeed) {
            globalIndicator.push(
                buildRpmData("RPM", data?.ChannelSpeed, maxRPMThrshhold)
            );
        }
        if (data["8KMixed"] && data["8KMixed"].valueInHealth) {
            globalIndicator.push(
                buildIndicatorData("Friction", data["8KMixed"], "valueInHealth")
            );
        }
        if (data?.GlobalMixed && data?.GlobalMixed.valueInHealth) {
            globalIndicator.push(
                buildIndicatorData(
                    "Global(Umbalance/Alignment/Loosness)",
                    data?.GlobalMixed,
                    "valueInHealth"
                )
            );
        }
        if (data["2KMixed"] && data["2KMixed"].valueInHealth) {
            globalIndicator.push(
                buildIndicatorData("Shaft/Clearance", data["2KMixed"], "valueInHealth")
            );
        }
    } else if (modelType === "Turbocharger") {
        if (data?.GlobalKurto && data?.GlobalKurto.valueInPercent !== undefined && data?.GlobalKurto.valueInPercent !== null) {
            globalIndicator.push(
                buildIndicatorData("Shock index", data?.GlobalKurto, "valueInPercent", true)
            );
        }
        if (data?.GlobalLevel && data?.GlobalLevel.value !== undefined && data?.GlobalLevel.value !== null) {
            globalIndicator.push(
                buildTorqueIndicatorData(
                    "Level RMS(mm/s)",
                    data.GlobalLevel?.value || 0,
                    10
                )
            );
        }
            if (data["4KMixed"] && data["4KMixed"].valueInPercent !== undefined && data["4KMixed"].valueInPercent !== null) {
            globalIndicator.push(
                buildIndicatorData("Bearing Damage", data["4KMixed"], "valueInPercent", true)
            );
        }
        if (data?.GlobalMixed && data?.GlobalMixed.valueInPercent !== undefined && data?.GlobalMixed.valueInPercent !== null) {
            globalIndicator.push(
                buildIndicatorData(
                    "Unbalance Instability",
                    data?.GlobalMixed,
                    "valueInPercent",
                    true
                )
            );
        }
    }

    return {
        globalIndicator,
        cylinder_specific_indicators,
        pressureReadings

    };
}

export function formatToUTC(value) {
    if (!value) return "";

    let date;

    try {
        // CASE 1: Already a Date object
        if (value instanceof Date) {
            date = value;
        }
        // CASE 2: Numeric or numeric string (Unix timestamps or ms)
        else if (!isNaN(Number(value))) {
            const num = Number(value);

            if (String(num).length === 10) {
                // 10-digit seconds → convert to ms
                date = new Date(num * 1000);
            } else if (String(num).length === 13) {
                // 13-digit milliseconds
                date = new Date(num);
            } else {
                // fallback: try as ms
                date = new Date(num);
            }
        }
        // CASE 3: String dates
        else if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return "";
            date = new Date(trimmed);
        }
        else {
            return "";
        }

        // Check invalid date
        if (isNaN(date.getTime())) return "";

        // Format in UTC
        const day = String(date.getUTCDate()).padStart(2, "0");

        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getUTCMonth()];

        const year = date.getUTCFullYear();

        const hours = String(date.getUTCHours()).padStart(2, "0");
        const minutes = String(date.getUTCMinutes()).padStart(2, "0");
        const seconds = String(date.getUTCSeconds()).padStart(2, "0");

        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
        return "";
    }
}
